import { cn } from "@/lib/utils"

/**
 * Shared icon components used across the marketing site.
 * Only contains icons that are duplicated in multiple files with identical SVG paths.
 */

interface IconProps {
  width?: number
  height?: number
  className?: string
}

/**
 * 5-point star icon, filled yellow (#FBBF24).
 * Used in footer (Google stars), testimonial cards, test detail pages, free-call page.
 */
export function StarIcon({
  width = 18,
  height = 18,
  className,
  ...props
}: IconProps) {
  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 20 20"
      fill="#FBBF24"
      aria-hidden="true"
      className={className}
      {...props}
    >
      <path d="M10 1l2.39 4.84 5.34.78-3.87 3.77.91 5.33L10 13.27l-4.77 2.51.91-5.33L2.27 6.68l5.34-.78L10 1z" />
    </svg>
  )
}

/**
 * Chevron-down icon (stroke-based, 20x20 viewBox).
 * Used in FAQ accordions, comparison tables, and other expand/collapse controls.
 */
export function ChevronDownIcon({
  width = 20,
  height = 20,
  className,
  ...props
}: IconProps) {
  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 20 20"
      fill="none"
      className={className}
      {...props}
    >
      <path
        d="M5 7.5L10 12.5L15 7.5"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

/**
 * Olive green filled circle with white checkmark (22x22, 20x20 viewBox).
 * Used in how-it-works pricing props, report page feature lists.
 */
export function CheckIcon({
  width = 22,
  height = 22,
  className,
  ...props
}: IconProps) {
  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 20 20"
      fill="none"
      className={className}
      {...props}
    >
      <circle cx="10" cy="10" r="9.5" fill="#7B8C3B" />
      <path
        d="M6.5 10l2.5 2.5 4.5-4.5"
        stroke="white"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

/**
 * Thin checkmark stroke, no background. Used where a minimal, text-weight
 * check is needed (provider checklist rows, plain-text confirmations).
 */
export function CheckmarkIcon({
  width = 20,
  height = 20,
  className,
  ...props
}: IconProps) {
  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 24 24"
      fill="none"
      className={className}
      aria-hidden="true"
      {...props}
    >
      <path
        d="M5 12.5l4 4 10-10"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

/**
 * Smaller olive green filled circle with white checkmark (16x16).
 * Used in pricing comparison grids, how-it-works feature lists.
 */
export function GreenCheck({
  width = 16,
  height = 16,
  className,
  ...props
}: IconProps) {
  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 16 16"
      fill="none"
      className={className}
      {...props}
    >
      <circle cx="8" cy="8" r="7.5" fill="#7B8C3B" />
      <path
        d="M5 8l2 2 4-4"
        stroke="white"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

/**
 * Olive green filled circle with white checkmark (16x16, design system version).
 * Used in PricingCard feature lists.
 */
export function CheckCircleIcon({
  className,
  size = 16,
}: {
  className?: string
  size?: number
}) {
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M16 8C16 9.58225 15.5308 11.129 14.6518 12.4446C13.7727 13.7602 12.5233 14.7855 11.0615 15.391C9.59966 15.9965 7.99113 16.155 6.43928 15.8463C4.88743 15.5376 3.46197 14.7757 2.34315 13.6569C1.22433 12.538 0.462403 11.1126 0.153721 9.56072C-0.15496 8.00887 0.00346625 6.40034 0.608967 4.93853C1.21447 3.47672 2.23985 2.22729 3.55544 1.34824C4.87103 0.469192 6.41775 0 8 0C10.121 0.00224 12.1546 0.845814 13.6544 2.34562C15.1542 3.84542 15.9978 5.87895 16 8Z"
        fill="#95A73B"
      />
      <path
        transform="translate(4.26, 5.58)"
        d="M7.33853 0.380213C7.30756 0.305507 7.26217 0.237636 7.20496 0.180484C7.1478 0.123268 7.07993 0.077878 7.00523 0.046909C6.93052 0.015941 6.85044 0 6.76957 0C6.6887 0 6.60862 0.015941 6.53392 0.046909C6.45921 0.077878 6.39134 0.123268 6.33419 0.180484L2.46188 4.05356L1.05111 2.64202C0.93564 2.52655 0.779027 2.46168 0.615727 2.46168C0.452426 2.46168 0.295813 2.52655 0.180342 2.64202C0.064871 2.75749 0 2.91411 0 3.07741C0 3.24071 0.064871 3.39732 0.180342 3.51279L2.0265 5.35894C2.08365 5.41616 2.15152 5.46155 2.22622 5.49252C2.30093 5.52349 2.38101 5.53943 2.46188 5.53943C2.54275 5.53943 2.62283 5.52349 2.69754 5.49252C2.77224 5.46155 2.84011 5.41616 2.89727 5.35894L7.20496 1.05125C7.26217 0.994101 7.30756 0.926231 7.33853 0.851524C7.3695 0.776818 7.38544 0.69674 7.38544 0.615869C7.38544 0.534998 7.3695 0.45492 7.33853 0.380213Z"
        fill="#F8F9EC"
      />
    </svg>
  )
}

/**
 * Neutral counterpart to `CheckCircleIcon`: the circle takes `currentColor`
 * (so a wrapper's text color sets it — e.g. `text-neutral-900`) with a white
 * tick. Used for neutral selection controls like the biomarker filter rows.
 */
export function CheckCircleNeutralIcon({
  className,
  size = 16,
}: {
  className?: string
  size?: number
}) {
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M16 8C16 9.58225 15.5308 11.129 14.6518 12.4446C13.7727 13.7602 12.5233 14.7855 11.0615 15.391C9.59966 15.9965 7.99113 16.155 6.43928 15.8463C4.88743 15.5376 3.46197 14.7757 2.34315 13.6569C1.22433 12.538 0.462403 11.1126 0.153721 9.56072C-0.15496 8.00887 0.00346625 6.40034 0.608967 4.93853C1.21447 3.47672 2.23985 2.22729 3.55544 1.34824C4.87103 0.469192 6.41775 0 8 0C10.121 0.00224 12.1546 0.845814 13.6544 2.34562C15.1542 3.84542 15.9978 5.87895 16 8Z"
        fill="currentColor"
      />
      <path
        transform="translate(4.26, 5.58)"
        d="M7.33853 0.380213C7.30756 0.305507 7.26217 0.237636 7.20496 0.180484C7.1478 0.123268 7.07993 0.077878 7.00523 0.046909C6.93052 0.015941 6.85044 0 6.76957 0C6.6887 0 6.60862 0.015941 6.53392 0.046909C6.45921 0.077878 6.39134 0.123268 6.33419 0.180484L2.46188 4.05356L1.05111 2.64202C0.93564 2.52655 0.779027 2.46168 0.615727 2.46168C0.452426 2.46168 0.295813 2.52655 0.180342 2.64202C0.064871 2.75749 0 2.91411 0 3.07741C0 3.24071 0.064871 3.39732 0.180342 3.51279L2.0265 5.35894C2.08365 5.41616 2.15152 5.46155 2.22622 5.49252C2.30093 5.52349 2.38101 5.53943 2.46188 5.53943C2.54275 5.53943 2.62283 5.52349 2.69754 5.49252C2.77224 5.46155 2.84011 5.41616 2.89727 5.35894L7.20496 1.05125C7.26217 0.994101 7.30756 0.926231 7.33853 0.851524C7.3695 0.776818 7.38544 0.69674 7.38544 0.615869C7.38544 0.534998 7.3695 0.45492 7.33853 0.380213Z"
        fill="#fff"
      />
    </svg>
  )
}

/** Speech bubble with a sparkle — the "Ask AI" segment of the search switch. */
export function ChatSparkIcon({
  width = 18,
  height = 18,
  className,
  ...props
}: IconProps) {
  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={className}
      {...props}
    >
      <path d="M21 14a2 2 0 0 1-2 2H8l-4 4V5a2 2 0 0 1 2-2h13a2 2 0 0 1 2 2z" />
      <path d="M12.5 7.2l.75 1.9 1.9.75-1.9.75-.75 1.9-.75-1.9-1.9-.75 1.9-.75z" />
    </svg>
  )
}

/**
 * AI sparkle — signals the "ask Ella" / AI-mode affordance. Google Material
 * Symbols `auto_awesome` (filled): the canonical "AI" sparkle — one large
 * four-point star plus two smaller ones.
 */
export function SparkleIcon({
  width = 18,
  height = 18,
  className,
  ...props
}: IconProps) {
  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
      className={className}
      {...props}
    >
      <path d="M19 9l1.25-2.75L23 5l-2.75-1.25L19 1l-1.25 2.75L15 5l2.75 1.25L19 9zm-7.5.5L9 4 6.5 9.5 1 12l5.5 2.5L9 20l2.5-5.5L17 12l-5.5-2.5z" />
    </svg>
  )
}

/**
 * Muted outline circle with an "x" — the "not included" counterpart to
 * `CheckCircleIcon`, used in side-by-side plan comparisons (e.g. the
 * membership-vs-one-off cards) to mark rows the cheaper option lacks.
 * Render in a soft neutral (`text-neutral-300`) so it reads as absence,
 * not error.
 */
export function XCircleIcon({
  className,
  size = 16,
}: {
  className?: string
  size?: number
}) {
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.5" />
      <path
        d="M14.5 9.5l-5 5M9.5 9.5l5 5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

/**
 * "+" inside an olive filled circle. Same circle treatment as
 * `CheckCircleIcon` so the two read as a matched pair, but the inner
 * glyph signals "additional" rather than "included". Used in
 * PricingCard feature lists on Advanced / Pro to reinforce the
 * "Everything in Core, plus …" headers.
 */
export function PlusCircleIcon({
  className,
  size = 16,
}: {
  className?: string
  size?: number
}) {
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <circle cx="8" cy="8" r="8" fill="#95A73B" />
      <rect x="3.6" y="7.15" width="8.8" height="1.7" rx="0.7" fill="#F8F9EC" />
      <rect x="7.15" y="3.6" width="1.7" height="8.8" rx="0.7" fill="#F8F9EC" />
    </svg>
  )
}

/**
 * Info icon with hover state (design system version).
 * Used in PricingCard for name info and feature info.
 */
/**
 * Follow-up icon (stethoscope/IV style). Used in PricingCard highlights.
 */
/**
 * Report/thermometer icon (design system). Used for "Quick & safe" trust badge.
 */
/**
 * Checklist icon (design system). Used in quick facts grid.
 */
export function ChecklistIcon({
  className,
  size = 24,
}: {
  className?: string
  size?: number
}) {
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M16.5357 9.92203H12.1406C11.9745 9.92085 11.8155 9.85433 11.698 9.73684C11.5805 9.61936 11.5139 9.46035 11.5128 9.2942C11.5128 8.95063 11.7971 8.66637 12.1395 8.66637H16.5357C16.7022 8.66637 16.8619 8.73251 16.9797 8.85026C17.0974 8.968 17.1636 9.12769 17.1636 9.2942C17.1636 9.46071 17.0974 9.6204 16.9797 9.73814C16.8619 9.85588 16.7022 9.92203 16.5357 9.92203ZM7.74557 10.5577C7.66303 10.5582 7.58122 10.5422 7.50493 10.5106C7.42864 10.4791 7.3594 10.4327 7.30125 10.3742L6.6745 9.74632C6.5577 9.62817 6.49219 9.46873 6.49219 9.30259C6.49219 9.13645 6.5577 8.97701 6.6745 8.85886C6.79261 8.7424 6.95182 8.67711 7.1177 8.67711C7.28358 8.67711 7.44279 8.7424 7.5609 8.85886L7.74557 9.04239L9.18485 7.6032C9.30301 7.4864 9.46246 7.4209 9.62861 7.4209C9.79476 7.4209 9.95421 7.4864 10.0724 7.6032C10.1892 7.72135 10.2547 7.88079 10.2547 8.04693C10.2547 8.21307 10.1892 8.37251 10.0724 8.49067L8.18877 10.3742C8.13057 10.4323 8.06147 10.4785 7.98543 10.51C7.90938 10.5415 7.82788 10.5577 7.74557 10.5577ZM16.5357 15.7829H12.1406C11.9745 15.7817 11.8155 15.7152 11.698 15.5977C11.5805 15.4802 11.5139 15.3212 11.5128 15.1551C11.5128 14.8115 11.7971 14.5272 12.1395 14.5272H16.5357C16.7022 14.5272 16.8619 14.5934 16.9797 14.7111C17.0974 14.8289 17.1636 14.9885 17.1636 15.1551C17.1636 15.3216 17.0974 15.4813 16.9797 15.599C16.8619 15.7167 16.7022 15.7829 16.5357 15.7829ZM7.74557 16.4185C7.66295 16.4189 7.5811 16.4027 7.5048 16.371C7.42851 16.3393 7.35931 16.2927 7.30125 16.2339L6.6745 15.6072C6.5577 15.489 6.49219 15.3296 6.49219 15.1634C6.49219 14.9973 6.5577 14.8379 6.6745 14.7197C6.79261 14.6033 6.95182 14.538 7.1177 14.538C7.28358 14.538 7.44279 14.6033 7.5609 14.7197L7.74557 14.9032L9.18485 13.4629C9.30301 13.3461 9.46246 13.2806 9.62861 13.2806C9.79476 13.2806 9.95421 13.3461 10.0724 13.4629C10.1892 13.5811 10.2547 13.7405 10.2547 13.9067C10.2547 14.0728 10.1892 14.2322 10.0724 14.3504L8.18877 16.2361C8.13057 16.2943 8.06147 16.3405 7.98543 16.372C7.90938 16.4035 7.82788 16.4197 7.74557 16.4197V16.4185Z"
        fill="currentColor"
      />
      <path
        d="M14.3421 20.9141H9.31805C4.773 20.9141 2.83008 18.9713 2.83008 14.4254V9.40275C2.83008 4.85686 4.773 2.91406 9.31805 2.91406H14.3421C18.8883 2.91406 20.8301 4.85686 20.8301 9.40275V14.4254C20.8312 18.9713 18.8883 20.9141 14.3421 20.9141ZM9.31805 4.17084C5.45907 4.17084 4.08581 5.54289 4.08581 9.40275V14.4254C4.08581 18.2852 5.45907 19.6584 9.31805 19.6584H14.3421C18.2011 19.6584 19.5743 18.2852 19.5743 14.4254V9.40275C19.5743 5.54289 18.2011 4.17084 14.3421 4.17084H9.31805Z"
        fill="currentColor"
      />
    </svg>
  )
}

/**
 * Smartphone icon — rounded rectangle with a home-button dot. Used in
 * trust signals to represent the interactive report.
 */
export function SmartphoneIcon({
  className,
  size = 24,
}: {
  className?: string
  size?: number
}) {
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect width="14" height="20" x="5" y="2" rx="2" ry="2" />
      <path d="M12 18h.01" />
    </svg>
  )
}

/**
 * Imaging / scanner icon — corner-bracketed square with a horizontal scan
 * line. Used in quick facts to represent a scan (e.g. low-dose CT).
 */
export function ImagingIcon({
  className,
  size = 24,
}: {
  className?: string
  size?: number
}) {
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M3 7V5a2 2 0 0 1 2-2h2" />
      <path d="M17 3h2a2 2 0 0 1 2 2v2" />
      <path d="M21 17v2a2 2 0 0 1-2 2h-2" />
      <path d="M7 21H5a2 2 0 0 1-2-2v-2" />
      <path d="M7 12h10" />
    </svg>
  )
}

/**
 * Radiation icon (design system). Used in quick facts grid.
 */
export function RadiationIcon({
  className,
  size = 24,
}: {
  className?: string
  size?: number
}) {
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M11.2402 13.714L9.7082 16.37C9.4202 16.868 8.7752 17.045 8.3382 16.67C7.36495 15.8366 6.70585 14.6955 6.4702 13.436C6.3652 12.87 6.8402 12.4 7.4152 12.4H10.5182M11.2502 11.099L9.7062 8.42803C9.4192 7.93103 9.5872 7.28403 10.1292 7.09303C10.7303 6.8816 11.363 6.77406 12.0002 6.77503C12.6562 6.77503 13.2852 6.88703 13.8702 7.09303C14.4122 7.28403 14.5802 7.93003 14.2932 8.42803L12.7772 11.054M13.5802 12.4L17.6252 12.399C17.6262 13.2104 17.4513 14.0123 17.1124 14.7495C16.7735 15.4867 16.2787 16.1416 15.6622 16.669C15.2262 17.044 14.5812 16.867 14.2932 16.369L12.7732 13.737"
        stroke="currentColor"
        strokeMiterlimit="10"
      />
      <path
        d="M11.9998 13.805C12.3727 13.805 12.7303 13.6569 12.994 13.3932C13.2576 13.1296 13.4058 12.7719 13.4058 12.399C13.4058 12.0261 13.2576 11.6685 12.994 11.4048C12.7303 11.1412 12.3727 10.993 11.9998 10.993C11.6267 10.993 11.269 11.1412 11.0052 11.405C10.7414 11.6688 10.5933 12.0265 10.5933 12.3995C10.5933 12.7726 10.7414 13.1303 11.0052 13.3941C11.269 13.6579 11.6267 13.806 11.9998 13.806V13.805Z"
        stroke="currentColor"
        strokeMiterlimit="10"
      />
      <path
        d="M12.0001 20.8C16.6393 20.8 20.4001 17.0392 20.4001 12.4C20.4001 7.76081 16.6393 4 12.0001 4C7.36091 4 3.6001 7.76081 3.6001 12.4C3.6001 17.0392 7.36091 20.8 12.0001 20.8Z"
        stroke="currentColor"
        strokeWidth="1.2"
      />
    </svg>
  )
}

/**
 * Report icon (design system) — document with lines, filled.
 * Source: design-system/components/ui/icons/report.tsx
 */
export function ReportIcon({
  className,
  size = 24,
}: {
  className?: string
  size?: number
}) {
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M19.8368 6.18091L16.0333 2.16785C15.9313 2.06027 15.7941 2 15.651 2H6.03514C4.91303 2 4 2.98068 4 4.18628V19.8137C4 21.0192 4.91289 22 6.03514 22H17.9649C19.087 22 20 21.0193 20 19.8137V6.599C20 6.44168 19.9412 6.29108 19.8368 6.18091ZM16.1965 3.98273L18.1208 6.01306H16.3699C16.2743 6.01306 16.1965 5.92944 16.1965 5.82675V3.98273ZM17.9649 20.8281H6.03514C5.51456 20.8281 5.09086 20.3731 5.09086 19.8137V4.18628C5.09086 3.62689 5.51442 3.17188 6.03514 3.17188H15.1058V5.82675C15.1058 6.57565 15.6729 7.18494 16.37 7.18494H18.9091V19.8137C18.9091 20.3731 18.4856 20.8281 17.9649 20.8281Z"
        fill="currentColor"
      />
      <path
        d="M15.7866 11.2969H8.09769C7.79642 11.2969 7.55226 11.5592 7.55226 11.8828C7.55226 12.2065 7.79642 12.4688 8.09769 12.4688H15.7866C16.0878 12.4688 16.332 12.2065 16.332 11.8828C16.332 11.5592 16.0878 11.2969 15.7866 11.2969Z"
        fill="currentColor"
      />
      <path
        d="M8.09769 14.8125H14.367C14.6683 14.8125 14.9124 14.5502 14.9124 14.2266C14.9124 13.9029 14.6683 13.6406 14.367 13.6406H8.09769C7.79642 13.6406 7.55226 13.9029 7.55226 14.2266C7.55226 14.5502 7.79642 14.8125 8.09769 14.8125Z"
        fill="currentColor"
      />
      <path
        d="M15.1234 15.9844H8.09769C7.79642 15.9844 7.55226 16.2467 7.55226 16.5703C7.55226 16.894 7.79642 17.1562 8.09769 17.1562H15.1234C15.4245 17.1562 15.6688 16.894 15.6688 16.5703C15.6688 16.2467 15.4245 15.9844 15.1234 15.9844Z"
        fill="currentColor"
      />
    </svg>
  )
}

export function Report2Icon({
  className,
  size = 24,
}: {
  className?: string
  size?: number
}) {
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M15.0226 4.02429H14.893V19.5174C14.8932 21.162 13.5952 22.5 11.9999 22.5C10.4045 22.5 9.10658 21.162 9.10658 19.5175L9.10667 4.0243H8.97747C8.7137 4.0243 8.5 3.80405 8.5 3.53213V1.99217C8.5 1.72025 8.7137 1.5 8.97747 1.5L15.0226 1.5C15.2863 1.5 15.5 1.72025 15.5 1.99217V3.53218C15.5 3.80405 15.2862 4.02429 15.0226 4.02429ZM13.9381 4.02429H10.0615V11.3809C11.617 11.1039 12.2926 11.6522 12.5857 12.1983C12.8027 12.602 13.2571 12.7616 13.9382 12.6748L13.9381 4.02429ZM10.0615 12.3827L10.0615 19.5174C10.0615 20.6192 10.931 21.5155 11.9998 21.5155C13.0687 21.5155 13.9382 20.6192 13.9382 19.5174L13.9382 13.6651C12.8145 13.7753 12.1156 13.3553 11.7504 12.6753C11.4908 12.1918 10.6868 12.25 10.0615 12.3827ZM9.45474 3.03987L14.5451 3.03987V2.48434L9.45474 2.48434V3.03987Z"
        fill="currentColor"
      />
    </svg>
  )
}

/**
 * Arrow up-right — Heroicons outline. Used for external / promotional callouts.
 */
/**
 * Ahead brand arrow mark — the logomark arrow (top-right pointing) used on
 * the "New: Ahead membership" pill and the blood-followup membership card.
 * Two filled paths (no stroke). Sized in CSS via width/height.
 */
export function AheadArrowIcon({
  className,
  size = 16,
}: {
  className?: string
  size?: number
}) {
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 15 16"
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M11.3742 0H0V1.99316L11.2938 1.71601C12.3158 1.69133 13.1522 2.51707 13.1311 3.53073L12.8861 15.7934H14.8593V3.4586C14.8593 1.54897 13.2976 0 11.3723 0H11.3742Z" />
      <path d="M9.37226 4.30513L0 14.3525L1.42584 15.8028L10.8517 5.66047C11.2325 5.25045 11.2019 4.61264 10.7828 4.24059C10.3732 3.87612 9.74546 3.9046 9.37226 4.30513Z" />
    </svg>
  )
}

export function ArrowUpRightIcon({
  className,
  size = 24,
}: {
  className?: string
  size?: number
}) {
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M4.5 19.5l15-15m0 0H8.25m11.25 0v11.25" />
    </svg>
  )
}

/**
 * Corner-down-right elbow (↳, Lucide `corner-down-right`). A quiet "reply /
 * ask this next" affordance — used as the leading glyph on Ella's follow-up
 * suggestion rows.
 */
export function ArrowReturnIcon({
  className,
  size = 24,
}: {
  className?: string
  size?: number
}) {
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="15 10 20 15 15 20" />
      <path d="M4 4v7a4 4 0 0 0 4 4h12" />
    </svg>
  )
}

/**
 * Arrow path — circular refresh/loop (Heroicons outline). Used for data
 * continuity / longitudinal tracking.
 */
export function ArrowPathIcon({
  className,
  size = 24,
}: {
  className?: string
  size?: number
}) {
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99" />
    </svg>
  )
}

/**
 * Swiss shield (outlined) with cross. Used for "Reviewed by Swiss physicians" trust badge.
 * Same icon as the top banner insurance shield, but as a reusable component.
 */
// Phosphor `shield-check` (regular). Copied per the repo's no-icon-library
// convention; renders filled, sized via `size`/`className`.
export function ShieldCheckIcon({
  className,
  size = 24,
}: {
  className?: string
  size?: number
}) {
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 256 256"
      fill="currentColor"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M208,40H48A16,16,0,0,0,32,56v56c0,52.72,25.52,84.67,46.93,102.19,23.06,18.86,46,25.26,47,25.53a8,8,0,0,0,4.2,0c1-.27,23.91-6.67,47-25.53C198.48,196.67,224,164.72,224,112V56A16,16,0,0,0,208,40Zm0,72c0,37.07-13.66,67.16-40.6,89.42A129.3,129.3,0,0,1,128,223.62a128.25,128.25,0,0,1-38.92-21.81C61.82,179.51,48,149.3,48,112l0-56,160,0ZM82.34,141.66a8,8,0,0,1,11.32-11.32L112,148.69l50.34-50.35a8,8,0,0,1,11.32,11.32l-56,56a8,8,0,0,1-11.32,0Z" />
    </svg>
  )
}

export function SwissShieldIcon({
  className,
  size = 28,
}: {
  className?: string
  size?: number
}) {
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 25 32"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M12.5 30.101C15.595 29.117 18.155 27.148 20.179 24.196C22.202 21.244 23.214 17.964 23.214 14.355V5.901L12.5 1.91L1.786 5.901V14.355C1.786 17.964 2.798 21.244 4.821 24.196C6.845 27.148 9.405 29.117 12.5 30.101Z"
        stroke="currentColor"
        strokeWidth="1.5"
      />
      <path
        d="M10.417 21.667H14.583V17.083H19.167V12.917H14.583V8.333H10.417V12.917H5.833V17.083H10.417Z"
        fill="currentColor"
      />
    </svg>
  )
}

/**
 * Dutch tricolor flag — monochrome, rendered like a black-and-white photo: the
 * red and blue bands are filled with `currentColor` at different opacities (red
 * lighter, blue darker) while the white band stays empty, so the three colours
 * read as tonal bands within a single-colour icon. Sits in a row of
 * `currentColor` trust icons. Mirrors `SwissShieldIcon`'s prop signature; used
 * by the market-aware `ReviewedByPhysiciansIcon` for the NL physician-review
 * trust badge. Uses rounded-corner path fills (not a clipPath) so multiple
 * instances on a page can't collide on a shared SVG id.
 */
export function DutchFlagIcon({
  className,
  size = 28,
}: {
  className?: string
  size?: number
}) {
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      {/* red band (top) */}
      <path
        d="M3 7.5A1.5 1.5 0 0 1 4.5 6H19.5A1.5 1.5 0 0 1 21 7.5V10H3Z"
        fill="currentColor"
        fillOpacity="0.35"
      />
      {/* blue band (bottom) */}
      <path
        d="M3 14H21V16.5A1.5 1.5 0 0 1 19.5 18H4.5A1.5 1.5 0 0 1 3 16.5Z"
        fill="currentColor"
        fillOpacity="0.9"
      />
      <rect
        x="3"
        y="6"
        width="18"
        height="12"
        rx="1.5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
    </svg>
  )
}

/** Add tests / MRI imaging — person with arms out + oval base, 44x44. Uses even-odd fill. */
export function AddTestsIcon({
  className,
  size = 24,
}: {
  className?: string
  size?: number
}) {
  return (
    <svg
      className={className}
      width={size}
      height={size}
      // Square viewBox keeps this icon at the same visual size as sibling
      // 24×24 icons. The source artwork is 24×29 (the decorative ground arc
      // under the figure extends to y=35) — we omit that arc below so the
      // icon fits cleanly in the square box without being clipped.
      viewBox="10 8 24 24"
      fill="none"
      aria-hidden="true"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M22.1264 14.449C22.7642 14.449 23.3876 14.2599 23.9178 13.9056C24.4481 13.5513 24.8614 13.0477 25.1055 12.4585C25.3495 11.8693 25.4134 11.2209 25.289 10.5954C25.1645 9.96995 24.8574 9.3954 24.4065 8.94444C23.9555 8.49348 23.381 8.18638 22.7555 8.06196C22.13 7.93754 21.4816 8.0014 20.8924 8.24545C20.3032 8.48951 19.7996 8.9028 19.4453 9.43307C19.091 9.96334 18.9019 10.5868 18.9019 11.2245C18.9019 12.0797 19.2416 12.8999 19.8463 13.5046C20.451 14.1093 21.2712 14.449 22.1264 14.449ZM22.1264 9.28981C22.5091 9.28981 22.8831 9.40328 23.2013 9.61587C23.5194 9.82846 23.7674 10.1306 23.9138 10.4841C24.0603 10.8377 24.0986 11.2267 24.0239 11.602C23.9493 11.9773 23.765 12.322 23.4944 12.5926C23.2239 12.8631 22.8791 13.0474 22.5038 13.1221C22.1285 13.1967 21.7395 13.1584 21.386 13.012C21.0325 12.8655 20.7303 12.6176 20.5177 12.2994C20.3052 11.9812 20.1917 11.6072 20.1917 11.2245C20.1917 10.7114 20.3955 10.2193 20.7584 9.85647C21.1212 9.49364 21.6133 9.28981 22.1264 9.28981ZM31.6796 21.4806L26.8202 15.971C26.5177 15.6278 26.1455 15.353 25.7286 15.1647C25.3116 14.9764 24.8593 14.879 24.4018 14.879H19.851C19.3935 14.8789 18.9413 14.9761 18.5244 15.1642C18.1074 15.3523 17.7352 15.627 17.4326 15.9699L12.5732 21.4806C12.2086 21.8418 12.0024 22.333 12 22.8462C11.9976 23.3594 12.1992 23.8526 12.5603 24.2172C12.9215 24.5818 13.4127 24.788 13.9259 24.7904C14.4391 24.7928 14.9323 24.5913 15.2969 24.2301L17.5659 22.4104L15.6172 29.8107C15.4123 30.2762 15.399 30.8037 15.5802 31.2789C15.7613 31.7542 16.1223 32.139 16.585 32.3501C17.0478 32.5612 17.575 32.5816 18.0527 32.4069C18.5303 32.2321 18.92 31.8763 19.1373 31.4165L22.1264 26.2723L25.1155 31.4218C25.3328 31.8817 25.7225 32.2375 26.2001 32.4122C26.6778 32.587 27.205 32.5666 27.6678 32.3555C28.1305 32.1444 28.4915 31.7596 28.6727 31.2843C28.8538 30.809 28.8405 30.2816 28.6356 29.816L26.6869 22.4136L28.9559 24.2301C29.3227 24.5788 29.8116 24.7699 30.3176 24.7624C30.8236 24.7549 31.3066 24.5494 31.6629 24.19C32.0192 23.8306 32.2205 23.3459 32.2236 22.8398C32.2267 22.3337 32.0314 21.8466 31.6796 21.4828V21.4806ZM30.752 23.2885C30.6921 23.3485 30.621 23.3961 30.5427 23.4285C30.4644 23.461 30.3805 23.4777 30.2957 23.4777C30.211 23.4777 30.127 23.461 30.0488 23.4285C29.9705 23.3961 29.8993 23.3485 29.8394 23.2885C29.8232 23.2714 29.8056 23.2556 29.7868 23.2412L25.97 20.1801C25.8642 20.0952 25.7344 20.0458 25.5989 20.0389C25.4634 20.0321 25.3292 20.0681 25.2154 20.1419C25.1016 20.2157 25.014 20.3236 24.965 20.4501C24.916 20.5765 24.9081 20.7153 24.9425 20.8465L27.4006 30.1901C27.4114 30.2268 27.4247 30.2627 27.4404 30.2976C27.5127 30.4526 27.5204 30.6301 27.4618 30.7908C27.4033 30.9516 27.2832 31.0825 27.1282 31.1547C26.9731 31.227 26.7957 31.2347 26.6349 31.1762C26.4742 31.1176 26.3432 30.9976 26.271 30.8425C26.2631 30.8248 26.2541 30.8075 26.2441 30.7909L22.6842 24.66C22.6276 24.5623 22.5462 24.4812 22.4483 24.4248C22.3504 24.3684 22.2394 24.3387 22.1264 24.3387C22.0134 24.3387 21.9024 24.3684 21.8045 24.4248C21.7066 24.4812 21.6252 24.5623 21.5686 24.66L18.0087 30.792C17.9987 30.8086 17.9897 30.8258 17.9818 30.8436C17.9096 30.9987 17.7786 31.1187 17.6179 31.1772C17.4571 31.2358 17.2797 31.2281 17.1246 31.1558C16.9696 31.0836 16.8495 30.9526 16.791 30.7919C16.7324 30.6311 16.7401 30.4537 16.8124 30.2986C16.8281 30.2638 16.8414 30.2279 16.8522 30.1912L19.3103 20.8476C19.3442 20.7168 19.3362 20.5788 19.2874 20.4528C19.2386 20.3269 19.1515 20.2195 19.0384 20.1457C18.9339 20.0769 18.812 20.0396 18.6869 20.0382C18.54 20.0381 18.3974 20.0881 18.2828 20.1801L14.466 23.2412C14.4472 23.2556 14.4296 23.2714 14.4134 23.2885C14.2923 23.4095 14.1282 23.4775 13.9571 23.4775C13.7859 23.4775 13.6218 23.4095 13.5008 23.2885C13.3798 23.1675 13.3118 23.0034 13.3118 22.8323C13.3118 22.6611 13.3798 22.497 13.5008 22.376L13.5288 22.347L18.3999 16.8234C18.5815 16.6176 18.8048 16.4528 19.055 16.3399C19.3052 16.2271 19.5765 16.1687 19.851 16.1688H24.4018C24.6763 16.1687 24.9476 16.2271 25.1978 16.3399C25.448 16.4528 25.6713 16.6176 25.8529 16.8234L30.7251 22.347L30.7531 22.376C30.813 22.436 30.8604 22.5071 30.8928 22.5855C30.9252 22.6638 30.9418 22.7477 30.9417 22.8325C30.9416 22.9172 30.9248 23.0011 30.8922 23.0794C30.8597 23.1576 30.812 23.2287 30.752 23.2885Z"
        fill="currentColor"
      />
    </svg>
  )
}

export function FollowUpIcon({
  className,
  size = 24,
}: {
  className?: string
  size?: number
}) {
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g clipPath="url(#clip0_90_649)">
        <path
          d="M13.2432 0.999943V2.82568"
          stroke="currentColor"
          strokeWidth="1.2"
          strokeMiterlimit="10"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M19.6348 0.999943V2.82568"
          stroke="currentColor"
          strokeWidth="1.2"
          strokeMiterlimit="10"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M14.2886 14.1869L16.4371 19.2577L18.5856 14.1869"
          stroke="currentColor"
          strokeWidth="1.2"
          strokeMiterlimit="10"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M19.6341 1.91293H20.7165C22.2328 1.91293 23.3276 3.36417 22.911 4.82207L21.0937 11.1821C20.4999 13.2603 18.6003 14.6931 16.4388 14.6931C14.2773 14.6931 12.3777 13.2603 11.7839 11.1821L9.96664 4.82207C9.55008 3.36417 10.6449 1.91293 12.1612 1.91293H13.2436"
          stroke="currentColor"
          strokeWidth="1.2"
          strokeMiterlimit="10"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M3.73828 17.4315V15.195C3.73828 13.4052 5.18928 11.9543 6.97916 11.9543C8.76904 11.9543 10.22 13.4052 10.22 15.195V19.8904C10.22 21.6078 11.6123 23 13.3298 23C15.0473 23 16.4396 21.6078 16.4396 19.8904V19.2572"
          stroke="currentColor"
          strokeWidth="1.2"
          strokeMiterlimit="10"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M1 20.1698C1 21.6823 2.22619 22.9084 3.73877 22.9084C5.25135 22.9084 6.47755 21.6823 6.47755 20.1698C6.47755 18.6573 5.25135 17.4312 3.73877 17.4312C2.22619 17.4312 1 18.6573 1 20.1698Z"
          stroke="currentColor"
          strokeWidth="1.2"
          strokeMiterlimit="10"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M3.05371 20.1698C3.05371 20.548 3.36027 20.8545 3.7384 20.8545C4.11654 20.8545 4.4231 20.548 4.4231 20.1698C4.4231 19.7917 4.11654 19.4852 3.7384 19.4852C3.36027 19.4852 3.05371 19.7917 3.05371 20.1698Z"
          fill="currentColor"
        />
      </g>
      <defs>
        <clipPath id="clip0_90_649">
          <rect width="24" height="24" fill="white" />
        </clipPath>
      </defs>
    </svg>
  )
}

/**
 * Impact area / target icon (concentric circles). Used in PricingCard highlights.
 */
export function ImpactAreaIcon({
  className,
  size = 24,
}: {
  className?: string
  size?: number
}) {
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <circle cx="12" cy="12" r="9.5" stroke="currentColor" />
      <circle cx="11.9993" cy="11.9993" r="6.64286" stroke="currentColor" />
      <circle cx="11.9986" cy="12.001" r="3.78571" stroke="currentColor" />
    </svg>
  )
}

/**
 * Location icon (DS exact) — map pin with circle inside. Used for clinics/places.
 */
export function LocationIcon({
  className,
  size = 24,
}: {
  className?: string
  size?: number
}) {
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M12.8433 21.8004C12.4318 22.3726 11.568 22.3726 11.1565 21.8004C8.44957 18.037 4.6499 13.312 4.6499 9.1888C4.6499 5.22489 7.94711 2 11.9999 2C16.0527 2 19.3499 5.22489 19.3499 9.1888C19.3499 13.312 15.5502 18.037 12.8433 21.8004Z"
        stroke="currentColor"
        strokeMiterlimit="10"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M12.0934 12.5C10.3565 12.5 8.94336 11.0868 8.94336 9.35C8.94336 7.61317 10.3565 6.2 12.0934 6.2C13.8302 6.2 15.2434 7.61317 15.2434 9.35C15.2434 11.0868 13.8302 12.5 12.0934 12.5Z"
        stroke="currentColor"
        strokeMiterlimit="10"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

/**
 * Outline check circle icon (DS version). Uses currentColor.
 * Apply text-light-green-500 for the standard olive green.
 */
export function CheckCircleOutlineIcon({
  className,
  size = 16,
}: {
  className?: string
  size?: number
}) {
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M11.5123 5.71846C11.5695 5.77561 11.6149 5.84348 11.6459 5.91819C11.6769 5.9929 11.6928 6.07297 11.6928 6.15384C11.6928 6.23472 11.6769 6.31479 11.6459 6.3895C11.6149 6.46421 11.5695 6.53208 11.5123 6.58923L7.20462 10.8969C7.14746 10.9541 7.07959 10.9995 7.00489 11.0305C6.93018 11.0615 6.8501 11.0774 6.76923 11.0774C6.68836 11.0774 6.60828 11.0615 6.53358 11.0305C6.45887 10.9995 6.391 10.9541 6.33385 10.8969L4.48769 9.05077C4.37222 8.9353 4.30735 8.77868 4.30735 8.61538C4.30735 8.45208 4.37222 8.29547 4.48769 8.18C4.60317 8.06453 4.75978 7.99966 4.92308 7.99966C5.08638 7.99966 5.24299 8.06453 5.35846 8.18L6.76923 9.59154L10.6415 5.71846C10.6987 5.66124 10.7666 5.61585 10.8413 5.58488C10.916 5.55392 10.9961 5.53798 11.0769 5.53798C11.1578 5.53798 11.2379 5.55392 11.3126 5.58488C11.3873 5.61585 11.4552 5.66124 11.5123 5.71846ZM16 8C16 9.58225 15.5308 11.129 14.6518 12.4446C13.7727 13.7602 12.5233 14.7855 11.0615 15.391C9.59966 15.9965 7.99113 16.155 6.43928 15.8463C4.88743 15.5376 3.46197 14.7757 2.34315 13.6569C1.22433 12.538 0.462403 11.1126 0.153721 9.56072C-0.15496 8.00887 0.00346627 6.40034 0.608967 4.93853C1.21447 3.47672 2.23985 2.22729 3.55544 1.34824C4.87103 0.469192 6.41775 0 8 0C10.121 0.00224 12.1546 0.845814 13.6544 2.34562C15.1542 3.84542 15.9978 5.87895 16 8ZM14.7692 8C14.7692 6.66117 14.3722 5.35241 13.6284 4.23922C12.8846 3.12602 11.8274 2.25839 10.5905 1.74605C9.35356 1.2337 7.99249 1.09965 6.67939 1.36084C5.36629 1.62203 4.16013 2.26674 3.21343 3.21343C2.26674 4.16012 1.62203 5.36629 1.36084 6.67939C1.09965 7.99249 1.2337 9.35356 1.74605 10.5905C2.2584 11.8274 3.12603 12.8846 4.23922 13.6284C5.35241 14.3722 6.66117 14.7692 8 14.7692C9.79469 14.7672 11.5153 14.0534 12.7843 12.7843C14.0534 11.5153 14.7672 9.79468 14.7692 8Z"
        fill="currentColor"
      />
    </svg>
  )
}

/** Chevron left — stroke, 24x24 viewBox. Navigation arrows, carousels. */
export function ChevronLeftIcon({
  width = 16,
  height = 16,
  className,
  ...props
}: IconProps) {
  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
      className={className}
      {...props}
    >
      <path
        d="M15 18l-6-6 6-6"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

/** Chevron right — stroke, 24x24 viewBox. Navigation arrows, carousels. */
export function ChevronRightIcon({
  width = 16,
  height = 16,
  className,
  ...props
}: IconProps) {
  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
      className={className}
      {...props}
    >
      <path
        d="M9 18l6-6-6-6"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

/** Small chevron right — stroke, 12x12 viewBox. Inline "learn more" links. */
export function ChevronRightSmIcon({
  width = 12,
  height = 12,
  className,
  ...props
}: IconProps) {
  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 12 12"
      fill="none"
      aria-hidden="true"
      className={className}
      {...props}
    >
      <path
        d="M4 2l4 4-4 4"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

/** Arrow left — stroke, 24x24 viewBox. Back navigation. */
export function ArrowLeftIcon({
  width = 14,
  height = 14,
  className,
  ...props
}: IconProps) {
  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
      className={className}
      {...props}
    >
      <path
        d="M19 12H5M12 19l-7-7 7-7"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

/** Map pin — stroke, 24x24 viewBox. Location markers. */
export function MapPinIcon({
  width = 14,
  height = 14,
  className,
  ...props
}: IconProps) {
  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
      className={className}
      {...props}
    >
      <path
        d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="12" cy="10" r="3" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  )
}

/** Clock — stroke, 24x24 viewBox. Opening hours, duration. */
export function ClockIcon({
  width = 20,
  height = 20,
  className,
  ...props
}: IconProps) {
  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
      className={className}
      {...props}
    >
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.5" />
      <path
        d="M12 6v6l4 2"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

/** Droplet — Lucide-style, 24x24 viewBox. Skin / hydration. */
export function DropletIcon({
  width = 20,
  height = 20,
  className,
  ...props
}: IconProps) {
  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
      className={className}
      {...props}
    >
      <path
        d="M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

/** Tram — Phosphor-style, 24x24 viewBox. Public transit. */
export function TramIcon({
  width = 20,
  height = 20,
  className,
  ...props
}: IconProps) {
  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
      className={className}
      {...props}
    >
      <rect
        x="5"
        y="3"
        width="14"
        height="14"
        rx="3"
        stroke="currentColor"
        strokeWidth="1.5"
      />
      <path
        d="M5 11h14"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
      <circle cx="9" cy="14" r="0.9" fill="currentColor" />
      <circle cx="15" cy="14" r="0.9" fill="currentColor" />
      <path
        d="M8 17l-2 4M16 17l2 4M9 3l-1-1M15 3l1-1"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  )
}

/** Plus — stroke, 20x20 viewBox. Expand/add actions. */
export function PlusIcon({
  width = 20,
  height = 20,
  className,
  ...props
}: IconProps) {
  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 20 20"
      fill="none"
      aria-hidden="true"
      className={className}
      {...props}
    >
      <path
        d="M10 4V16M4 10H16"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  )
}

/** Search / magnifying glass — stroke, 24x24 viewBox. */
export function SearchIcon({
  width = 18,
  height = 18,
  className,
  ...props
}: IconProps) {
  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={className}
      {...props}
    >
      <circle cx="11" cy="11" r="8" />
      <path d="M21 21l-4.35-4.35" />
    </svg>
  )
}

/** Close / X — stroke, 24x24 viewBox. Modal close, dismiss. */
export function CloseIcon({
  width = 14,
  height = 14,
  className,
  ...props
}: IconProps) {
  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
      className={className}
      {...props}
    >
      <path
        d="M18 6L6 18M6 6l12 12"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  )
}

/** Google "G" logo — filled, 16x16. Google reviews badge. */
export function GoogleIcon({
  width = 16,
  height = 16,
  className,
  ...props
}: IconProps) {
  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden="true"
      className={className}
      {...props}
    >
      <path
        d="M15.04 8.167c0-.52-.047-1.02-.133-1.5H8v2.84h3.947a3.374 3.374 0 01-1.467 2.213v1.84h2.38c1.387-1.28 2.187-3.16 2.187-5.393z"
        fill="#4285F4"
      />
      <path
        d="M8 16c1.98 0 3.64-.66 4.853-1.78l-2.38-1.84c-.653.44-1.493.7-2.473.7-1.907 0-3.52-1.287-4.1-3.02H1.44v1.9C2.653 14.26 5.107 16 8 16z"
        fill="#34A853"
      />
      <path
        d="M3.9 9.553A4.788 4.788 0 013.647 8c0-.54.093-1.06.253-1.553v-1.9H1.44A8.003 8.003 0 00.667 8c0 1.287.307 2.507.773 3.553l2.46-1.9z"
        fill="#FBBC05"
      />
      <path
        d="M8 3.18c1.08 0 2.04.373 2.807 1.1l2.1-2.1C11.64.827 9.98 0 8 0 5.107 0 2.653 1.74 1.44 4.287l2.46 1.9C4.48 4.74 6.093 3.18 8 3.18z"
        fill="#EA4335"
      />
    </svg>
  )
}

/** Chevron down small — 16x16 viewBox. Smaller expand/collapse triggers. */
export function ChevronDownSmIcon({
  width = 16,
  height = 16,
  className,
  ...props
}: IconProps) {
  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden="true"
      className={className}
      {...props}
    >
      <path
        d="M4 6l4 4 4-4"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

/** Stethoscope — stroke, 24x24. Medical/blood test context. */
export function StethoscopeIcon({
  width = 24,
  height = 24,
  className,
  ...props
}: IconProps) {
  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
      className={className}
      {...props}
    >
      <path
        d="M4.8 2.8v5.6a7.2 7.2 0 0 0 14.4 0V2.8"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
      <path
        d="M4.8 2.8a1.2 1.2 0 1 0 0-2.4 1.2 1.2 0 0 0 0 2.4zM19.2 2.8a1.2 1.2 0 1 0 0-2.4 1.2 1.2 0 0 0 0 2.4z"
        fill="currentColor"
      />
      <circle
        cx="12"
        cy="19.2"
        r="3.2"
        stroke="currentColor"
        strokeWidth="1.5"
      />
      <path
        d="M12 16V8.4"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  )
}

/**
 * Classic stethoscope — stroke, 24x24: earpieces top right, tubing down and
 * round to a chest piece bottom left. Trust badge "reviewed by physicians" on
 * the DE blood-test ad LPs; `StethoscopeIcon` above is the abstract variant.
 */
export function StethoscopeOutlineIcon({
  className,
  size = 24,
}: {
  className?: string
  size?: number
}) {
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M11.5 3H12v4.5a3.5 3.5 0 0 0 7 0V3h.5M15.5 11v5a4 4 0 0 1-8 0"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle
        cx="5.25"
        cy="16"
        r="2.25"
        stroke="currentColor"
        strokeWidth="1.5"
      />
      <circle cx="5.25" cy="16" r="0.5" fill="currentColor" />
    </svg>
  )
}

/** Plus in a circle — stroke, 24x24. "Add more tests" trust badge. */
export function PlusCircleOutlineIcon({
  className,
  size = 24,
}: {
  className?: string
  size?: number
}) {
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <circle
        cx="12"
        cy="12"
        r="9.25"
        stroke="currentColor"
        strokeWidth="1.5"
      />
      <path
        d="M12 7.5v9M7.5 12h9"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  )
}

/** Person/user — stroke, 24x24. User profile, "for whom" context. */
export function PersonIcon({
  width = 24,
  height = 24,
  className,
  ...props
}: IconProps) {
  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
      className={className}
      {...props}
    >
      <circle cx="12" cy="8" r="4" stroke="currentColor" strokeWidth="1.5" />
      <path
        d="M20 21a8 8 0 1 0-16 0"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  )
}

/**
 * UserCircle — person-in-a-circle for the navbar login control. Same silhouette
 * family as `SearchIcon` (24 viewBox, round caps) but tuned to sit ~10% larger
 * and ~10% thinner than the search glyph: geometry is scaled to r9.9 (footprint
 * ~19.8u vs search's 18u) and the stroke is 1.8 (vs search's 2). Distinct from
 * the `PersonIcon` avatar silhouette above.
 */
export function UserCircleIcon({
  width = 24,
  height = 24,
  className,
  ...props
}: IconProps) {
  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={className}
      {...props}
    >
      <circle cx="12" cy="12" r="9.9" />
      <circle cx="12" cy="10.02" r="3.96" />
      <path d="M17.94 19.92a5.94 5.94 0 0 0-11.88 0" />
    </svg>
  )
}

/** Phone — stroke, 24x24. Contact, footer. */
export function PhoneIcon({
  width = 16,
  height = 16,
  className,
  ...props
}: IconProps) {
  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={className}
      {...props}
    >
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
    </svg>
  )
}

/** Globe — stroke, 16x16. Locale picker, language context. */
export function GlobeIcon({
  width = 16,
  height = 16,
  className,
  ...props
}: IconProps) {
  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      aria-hidden="true"
      className={className}
      {...props}
    >
      <circle cx="8" cy="8" r="6.5" />
      <ellipse cx="8" cy="8" rx="3" ry="6.5" />
      <line x1="1.5" y1="8" x2="14.5" y2="8" />
    </svg>
  )
}

export function InfoIcon({
  className,
  size = 22,
}: {
  className?: string
  size?: number
}) {
  return (
    <span
      className={cn(
        "group/info -m-[9px] inline-flex min-h-[40px] min-w-[40px] cursor-pointer items-center justify-center rounded-full",
        className
      )}
    >
      <svg
        width={size}
        height={size}
        viewBox="0 0 22 22"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <circle
          cx="11"
          cy="11"
          r="11"
          className="fill-neutral-100 transition-colors duration-200 group-hover/info:fill-neutral-200"
        />
        <path
          transform="translate(10.0672, 9.8516)"
          d="M1.7368 0.86837V5.78947C1.7368 6.26907 1.348 6.65787 0.8684 6.65787C0.3888 6.65787 0 6.26907 0 5.78947V0.86837C0 0.38877 0.3888 0 0.8684 0C1.348 0 1.7368 0.38877 1.7368 0.86837Z"
          className="fill-neutral-500 transition-colors duration-200 group-hover/info:fill-neutral-700"
        />
        <path
          transform="translate(9.999, 5.9994)"
          d="M1.8706 0.935C1.8706 1.19996 1.78 1.42209 1.5988 1.60137C1.4177 1.78065 1.1966 1.87029 0.9353 1.87029C0.674 1.87029 0.4528 1.78065 0.2718 1.60137C0.0906 1.42209 0 1.19996 0 0.935C0 0.67004 0.0896 0.44791 0.2689 0.26863C0.4482 0.08955 0.6703 0 0.9353 0C1.2003 0 1.4224 0.08955 1.6017 0.26863C1.7809 0.44791 1.8706 0.67004 1.8706 0.935Z"
          className="fill-neutral-500 transition-colors duration-200 group-hover/info:fill-neutral-700"
        />
      </svg>
    </span>
  )
}

/**
 * Category icon — 3 squares + circle, suggests grouping different elements together.
 * Source: design-system/components/ui/icons/category.tsx
 */
export function CategoryIcon({
  className,
  size = 24,
}: {
  className?: string
  size?: number
}) {
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g clipPath="url(#category-clip)">
        <path
          d="M9.77086 11.0216H6.38926C6.04687 11.0213 5.71859 10.8852 5.47645 10.6431C5.23431 10.401 5.09808 10.0728 5.09766 9.73041V6.39081C5.09808 6.04842 5.23431 5.72019 5.47645 5.47812C5.71859 5.23605 6.04687 5.09993 6.38926 5.09961H9.77086C10.1132 5.10003 10.4413 5.23621 10.6834 5.47826C10.9255 5.72032 11.0616 6.04849 11.0621 6.39081V9.73041C11.0616 10.0727 10.9255 10.4009 10.6834 10.643C10.4413 10.885 10.1132 11.0212 9.77086 11.0216ZM6.38926 6.09961C6.31202 6.09971 6.23797 6.13042 6.18332 6.18499C6.12867 6.23957 6.09787 6.31357 6.09766 6.39081V9.73041C6.09787 9.80764 6.12867 9.88165 6.18332 9.93623C6.23797 9.9908 6.31202 10.0215 6.38926 10.0216H9.77086C9.84805 10.0215 9.92206 9.99079 9.97665 9.9362C10.0312 9.88161 10.062 9.80761 10.0621 9.73041V6.39081C10.062 6.31361 10.0312 6.2396 9.97665 6.18502C9.92206 6.13043 9.84805 6.09972 9.77086 6.09961H6.38926Z"
          fill="currentColor"
        />
        <path
          d="M9.79039 18.8986H6.40879C6.06636 18.8981 5.73809 18.7619 5.49596 18.5198C5.25383 18.2777 5.11761 17.9494 5.11719 17.607V14.2678C5.11761 13.9254 5.25384 13.5971 5.49598 13.3551C5.73812 13.113 6.0664 12.9769 6.40879 12.9766H9.79039C10.1326 12.9772 10.4605 13.1135 10.7024 13.3555C10.9443 13.5975 11.0804 13.9256 11.0808 14.2678V17.607C11.0805 17.9492 10.9445 18.2774 10.7026 18.5195C10.4607 18.7616 10.1326 18.8979 9.79039 18.8986ZM6.40879 13.9766C6.33155 13.9767 6.2575 14.0074 6.20285 14.0619C6.1482 14.1165 6.1174 14.1905 6.11719 14.2678V17.607C6.11729 17.6843 6.14805 17.7584 6.20271 17.813C6.25737 17.8677 6.33148 17.8985 6.40879 17.8986H9.79039C9.86762 17.8984 9.94163 17.8675 9.9962 17.8129C10.0508 17.7582 10.0815 17.6842 10.0816 17.607V14.2678C10.0815 14.1906 10.0508 14.1166 9.99618 14.062C9.94159 14.0074 9.86759 13.9767 9.79039 13.9766H6.40879Z"
          fill="currentColor"
        />
        <path
          d="M17.6068 18.8988H14.2252C13.8828 18.8984 13.5545 18.7622 13.3124 18.52C13.0702 18.2779 12.934 17.9496 12.9336 17.6072V14.268C12.934 13.9256 13.0702 13.5974 13.3124 13.3553C13.5545 13.1133 13.8828 12.9771 14.2252 12.9768H17.6068C17.9491 12.9772 18.2773 13.1134 18.5193 13.3555C18.7614 13.5975 18.8976 13.9257 18.898 14.268V17.6072C18.8977 17.9496 18.7616 18.2779 18.5195 18.52C18.2774 18.7622 17.9492 18.8984 17.6068 18.8988ZM14.2252 13.9788C14.148 13.9789 14.0739 14.0096 14.0193 14.0642C13.9646 14.1188 13.9338 14.1928 13.9336 14.27V17.6092C13.9337 17.6865 13.9645 17.7606 14.0191 17.8153C14.0738 17.8699 14.1479 17.9007 14.2252 17.9008H17.6068C17.684 17.9006 17.758 17.8698 17.8126 17.8151C17.8672 17.7605 17.8979 17.6864 17.898 17.6092V14.268C17.8979 14.1908 17.8672 14.1168 17.8126 14.0622C17.758 14.0076 17.684 13.9769 17.6068 13.9768L14.2252 13.9788Z"
          fill="currentColor"
        />
        <path
          d="M15.9146 11.0215C15.3301 11.0171 14.76 10.8399 14.2761 10.512C13.7922 10.1841 13.4163 9.7203 13.1956 9.17906C12.975 8.63783 12.9195 8.04337 13.0361 7.47065C13.1528 6.89792 13.4365 6.37256 13.8513 5.96079C14.2661 5.54903 14.7936 5.26929 15.3671 5.15685C15.9407 5.04441 16.5347 5.1043 17.0743 5.32897C17.6139 5.55363 18.0749 5.93302 18.3992 6.4193C18.7235 6.90559 18.8965 7.47699 18.8966 8.06148C18.8926 8.84913 18.5765 9.60307 18.0175 10.158C17.4585 10.7129 16.7022 11.0234 15.9146 11.0215ZM15.9146 6.10148C15.5279 6.10582 15.1511 6.22445 14.8317 6.44244C14.5123 6.66043 14.2645 6.96804 14.1195 7.32654C13.9745 7.68505 13.9389 8.07842 14.0169 8.45717C14.095 8.83591 14.2834 9.18309 14.5584 9.455C14.8334 9.72692 15.1826 9.91141 15.5622 9.98528C15.9418 10.0591 16.3348 10.0191 16.6916 9.87009C17.0485 9.72111 17.3533 9.4699 17.5677 9.14808C17.7821 8.82625 17.8965 8.44819 17.8966 8.06148C17.8934 7.53864 17.6829 7.03841 17.3113 6.67058C16.9397 6.30274 16.4374 6.09735 15.9146 6.09948V6.10148Z"
          fill="currentColor"
        />
      </g>
      <defs>
        <clipPath id="category-clip">
          <rect
            width="14"
            height="14"
            fill="white"
            transform="translate(5 5)"
          />
        </clipPath>
      </defs>
    </svg>
  )
}

/**
 * Body figure icon — stylized person with arms raised. From the DS `PersonIcon`,
 * renamed here to avoid clashing with marketing-site's existing user-silhouette PersonIcon.
 * Source: design-system/components/ui/icons/person.tsx
 */
export function BodyFigureIcon({
  className,
  size = 24,
}: {
  className?: string
  size?: number
}) {
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 32 32"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M16.1264 8.44904C16.7642 8.44904 17.3876 8.25993 17.9178 7.90561C18.4481 7.5513 18.8614 7.0477 19.1055 6.45849C19.3495 5.86929 19.4134 5.22094 19.289 4.59545C19.1645 3.96995 18.8574 3.3954 18.4065 2.94444C17.9555 2.49348 17.381 2.18638 16.7555 2.06196C16.13 1.93754 15.4816 2.0014 14.8924 2.24545C14.3032 2.48951 13.7996 2.9028 13.4453 3.43307C13.091 3.96334 12.9019 4.58677 12.9019 5.22452C12.9019 6.07972 13.2416 6.89989 13.8463 7.5046C14.451 8.10932 15.2712 8.44904 16.1264 8.44904ZM16.1264 3.28981C16.5091 3.28981 16.8831 3.40328 17.2013 3.61587C17.5194 3.82846 17.7674 4.13062 17.9138 4.48414C18.0603 4.83766 18.0986 5.22667 18.0239 5.60197C17.9493 5.97726 17.765 6.32199 17.4944 6.59257C17.2239 6.86314 16.8791 7.04741 16.5038 7.12206C16.1285 7.19671 15.7395 7.1584 15.386 7.01196C15.0325 6.86553 14.7303 6.61755 14.5177 6.29939C14.3052 5.98123 14.1917 5.60717 14.1917 5.22452C14.1917 4.7114 14.3955 4.2193 14.7584 3.85647C15.1212 3.49364 15.6133 3.28981 16.1264 3.28981ZM25.6796 15.4806L20.8202 9.97102C20.5177 9.62784 20.1455 9.35299 19.7286 9.1647C19.3116 8.97642 18.8593 8.87902 18.4018 8.87898H13.851C13.3935 8.87889 12.9413 8.97612 12.5244 9.16421C12.1074 9.35231 11.7352 9.62696 11.4326 9.96994L6.57323 15.4806C6.20863 15.8418 6.00244 16.333 6.00002 16.8462C5.9976 17.3594 6.19915 17.8526 6.56033 18.2172C6.92151 18.5818 7.41273 18.788 7.92593 18.7904C8.43913 18.7928 8.93227 18.5913 9.29687 18.2301L11.5659 16.4104L9.61717 23.8107C9.41231 24.2762 9.39901 24.8037 9.58015 25.2789C9.76129 25.7542 10.1223 26.139 10.585 26.3501C11.0478 26.5612 11.575 26.5816 12.0527 26.4069C12.5303 26.2321 12.92 25.8763 13.1373 25.4165L16.1264 20.2723L19.1155 25.4218C19.3328 25.8817 19.7225 26.2375 20.2001 26.4122C20.6778 26.587 21.205 26.5666 21.6678 26.3555C22.1305 26.1444 22.4915 25.7596 22.6727 25.2843C22.8538 24.809 22.8405 24.2816 22.6356 23.816L20.6869 16.4136L22.9559 18.2301C23.3227 18.5788 23.8116 18.7699 24.3176 18.7624C24.8236 18.7549 25.3066 18.5494 25.6629 18.19C26.0192 17.8306 26.2205 17.3459 26.2236 16.8398C26.2267 16.3337 26.0314 15.8466 25.6796 15.4828V15.4806ZM24.752 17.2885C24.6921 17.3485 24.621 17.3961 24.5427 17.4285C24.4644 17.461 24.3805 17.4777 24.2957 17.4777C24.211 17.4777 24.127 17.461 24.0488 17.4285C23.9705 17.3961 23.8993 17.3485 23.8394 17.2885C23.8232 17.2714 23.8056 17.2556 23.7868 17.2412L19.97 14.1801C19.8642 14.0952 19.7344 14.0458 19.5989 14.0389C19.4634 14.0321 19.3292 14.0681 19.2154 14.1419C19.1016 14.2157 19.014 14.3236 18.965 14.4501C18.916 14.5765 18.9081 14.7153 18.9425 14.8465L21.4006 24.1901C21.4114 24.2268 21.4247 24.2627 21.4404 24.2976C21.5127 24.4526 21.5204 24.6301 21.4618 24.7908C21.4033 24.9516 21.2832 25.0825 21.1282 25.1547C20.9731 25.227 20.7957 25.2347 20.6349 25.1762C20.4742 25.1176 20.3432 24.9976 20.271 24.8425C20.2631 24.8248 20.2541 24.8075 20.2441 24.7909L16.6842 18.66C16.6276 18.5623 16.5462 18.4812 16.4483 18.4248C16.3504 18.3684 16.2394 18.3387 16.1264 18.3387C16.0134 18.3387 15.9024 18.3684 15.8045 18.4248C15.7066 18.4812 15.6252 18.5623 15.5686 18.66L12.0087 24.792C11.9987 24.8086 11.9897 24.8258 11.9818 24.8436C11.9096 24.9987 11.7786 25.1187 11.6179 25.1772C11.4571 25.2358 11.2797 25.2281 11.1246 25.1558C10.9696 25.0836 10.8495 24.9526 10.791 24.7919C10.7324 24.6311 10.7401 24.4537 10.8124 24.2986C10.8281 24.2638 10.8414 24.2279 10.8522 24.1912L13.3103 14.8476C13.3442 14.7168 13.3362 14.5788 13.2874 14.4528C13.2386 14.3269 13.1515 14.2195 13.0384 14.1457C12.9339 14.0769 12.812 14.0396 12.6869 14.0382C12.54 14.0381 12.3974 14.0881 12.2828 14.1801L8.46602 17.2412C8.44724 17.2556 8.42963 17.2714 8.41335 17.2885C8.29234 17.4095 8.12822 17.4775 7.95708 17.4775C7.78595 17.4775 7.62182 17.4095 7.50081 17.2885C7.3798 17.1675 7.31182 17.0034 7.31182 16.8323C7.31182 16.6611 7.3798 16.497 7.50081 16.376L7.52876 16.347L12.3999 10.8234C12.5815 10.6176 12.8048 10.4528 13.055 10.3399C13.3052 10.2271 13.5765 10.1687 13.851 10.1688H18.4018C18.6763 10.1687 18.9476 10.2271 19.1978 10.3399C19.448 10.4528 19.6713 10.6176 19.8529 10.8234L24.7251 16.347L24.7531 16.376C24.813 16.436 24.8604 16.5071 24.8928 16.5855C24.9252 16.6638 24.9418 16.7477 24.9417 16.8325C24.9416 16.9172 24.9248 17.0011 24.8922 17.0794C24.8597 17.1576 24.812 17.2287 24.752 17.2885Z"
        fill="currentColor"
        fillOpacity={0.8}
      />
      <path
        opacity={0.4}
        d="M24.9444 22C26.8446 22.7431 28 23.7243 28 24.8C28 27.1196 22.6274 29 16 29C9.37258 29 4 27.1196 4 24.8C4 23.7243 5.15541 22.7431 7.05557 22"
        stroke="currentColor"
        strokeWidth={1.3}
        strokeLinecap="round"
      />
    </svg>
  )
}

/**
 * Lifestyle icon — three leaves/figures suggesting holistic wellness/lifestyle.
 * Source: design-system/components/ui/icons/lifestyle.tsx
 */
export function LifestyleIcon({
  className,
  size = 24,
}: {
  className?: string
  size?: number
}) {
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M12.0002 17.9562C8.09528 14.1024 8.09561 7.85343 12.0006 4C15.9059 7.85413 15.9055 14.1024 12.0002 17.9562ZM12.0002 17.9562C14.6522 17.9562 17.1961 16.917 19.0714 15.0663C20.9467 13.2155 22 10.7052 22 8.08803C19.348 8.08803 16.8044 9.12754 14.9291 10.9783C14.9291 13.5958 13.8756 16.1055 12.0002 17.9562ZM12.0002 17.9562C9.34829 17.9562 6.80429 16.917 4.92896 15.0663C3.05368 13.2155 2 10.7052 2 8.08803C4.65195 8.08803 7.19594 9.12754 9.07089 10.9783C9.07089 13.5958 10.125 16.1055 12.0002 17.9562ZM12.0002 17.9562C9.8243 17.9562 7.72029 17.2558 5.99928 15.9817C4.53631 16.187 3.126 16.8453 2.00033 17.9562C4.76162 20.6813 9.23929 20.6813 12.0002 17.9562ZM12.0002 17.9562C14.1766 17.9562 16.2794 17.2558 18.0004 15.9817C19.4637 16.187 20.874 16.8453 21.9997 17.9562C19.2383 20.6813 14.7615 20.6813 12.0002 17.9562ZM5.82742 8.84085C5.82775 7.55692 6.08144 6.27364 6.58874 5.06469C7.81374 5.56538 8.91339 6.29567 9.83305 7.20328C9.33505 8.38917 9.07169 9.67114 9.07169 10.9797C8.13407 10.0547 7.0294 9.332 5.82742 8.84085ZM14.1686 7.20295C15.0879 6.29567 16.1873 5.56505 17.4122 5.06436C17.9196 6.27331 18.1742 7.55724 18.1742 8.84085C16.9719 9.332 15.8673 10.0547 14.93 10.9797C14.93 9.67114 14.6663 8.38917 14.1686 7.20295Z"
        stroke="currentColor"
        strokeWidth={1.2}
        strokeMiterlimit={10}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

/**
 * Exercise icon — dumbbell. Used in recommendation pills.
 * Source: design-system/components/ui/icons/exercise.tsx
 */
export function ExerciseIcon({
  className,
  size = 24,
}: {
  className?: string
  size?: number
}) {
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M6.52148 4.4248C7.32427 4.4248 7.96582 5.13815 7.96582 6V9.4248H16.0342V6C16.0342 5.13815 16.6757 4.4248 17.4785 4.4248H18.3916C19.1942 4.42498 19.8359 5.13825 19.8359 6V7.48633C19.9564 7.4485 20.0842 7.4248 20.2178 7.4248H21.1309C21.9334 7.42506 22.5752 8.1383 22.5752 9V15C22.5752 15.8617 21.9334 16.5749 21.1309 16.5752H20.2178C20.0841 16.5752 19.9565 16.5506 19.8359 16.5127V18C19.8359 18.8617 19.1942 19.575 18.3916 19.5752H17.4785C16.6757 19.5752 16.0342 18.8619 16.0342 18V14.5752H7.96582V18C7.96582 18.8619 7.32427 19.5752 6.52148 19.5752H5.6084C4.80575 19.575 4.16406 18.8617 4.16406 18V16.5127C4.04348 16.5506 3.91593 16.5752 3.78223 16.5752H2.86914C2.06655 16.5749 1.4248 15.8617 1.4248 15V9C1.4248 8.1383 2.06655 7.42506 2.86914 7.4248H3.78223C3.91583 7.4248 4.04357 7.4485 4.16406 7.48633V6C4.16406 5.13825 4.80575 4.42498 5.6084 4.4248H6.52148ZM5.6084 5.5752C5.40468 5.57537 5.22754 5.75947 5.22754 6V18C5.22754 18.2405 5.40468 18.4246 5.6084 18.4248H6.52148C6.72533 18.4248 6.90332 18.2406 6.90332 18V6C6.90332 5.75935 6.72533 5.5752 6.52148 5.5752H5.6084ZM17.4785 5.5752C17.2747 5.5752 17.0967 5.75935 17.0967 6V18C17.0967 18.2406 17.2747 18.4248 17.4785 18.4248H18.3916C18.5953 18.4246 18.7725 18.2405 18.7725 18V6C18.7725 5.75947 18.5953 5.57537 18.3916 5.5752H17.4785ZM2.86914 8.5752C2.66547 8.57545 2.48828 8.75952 2.48828 9V15C2.48828 15.2405 2.66547 15.4245 2.86914 15.4248H3.78223C3.98607 15.4248 4.16406 15.2406 4.16406 15V9C4.16406 8.75935 3.98607 8.5752 3.78223 8.5752H2.86914ZM20.2178 8.5752C20.0139 8.5752 19.8359 8.75935 19.8359 9V15C19.8359 15.2406 20.0139 15.4248 20.2178 15.4248H21.1309C21.3345 15.4245 21.5117 15.2405 21.5117 15V9C21.5117 8.75952 21.3345 8.57545 21.1309 8.5752H20.2178ZM7.96582 13.4248H16.0342V10.5752H7.96582V13.4248Z"
        fill="currentColor"
        stroke="currentColor"
        strokeWidth="0.15"
      />
    </svg>
  )
}

/**
 * Nutrition icon — stylized heart/leaf shape. Used in recommendation pills.
 * Source: design-system/components/ui/icons/nutrition.tsx
 */
export function NutritionIcon({
  className,
  size = 24,
}: {
  className?: string
  size?: number
}) {
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M18.8438 2.98242C19.1219 2.88511 19.429 3.01221 19.5566 3.27734C19.586 3.33836 20.2591 4.74974 20.6934 6.76465C20.9492 7.95241 21.0689 9.10623 21.0479 10.1934C21.0213 11.5679 20.769 12.8409 20.2979 13.9756C19.8268 15.1105 19.1028 16.188 18.1475 17.1787C17.3917 17.9622 16.4897 18.6941 15.4668 19.3535C13.7317 20.4719 12.2552 20.9952 12.1914 21.0176C12.1294 21.0392 12.0647 21.0498 12 21.0498C11.9353 21.0498 11.8705 21.0392 11.8086 21.0176C11.7446 20.9952 10.2683 20.472 8.5332 19.3535C7.51026 18.6941 6.60815 17.9622 5.85254 17.1787C4.8972 16.188 4.17336 15.1104 3.70215 13.9756C3.23109 12.8409 2.97868 11.5679 2.95215 10.1934C2.93119 9.10623 3.05077 7.95241 3.30664 6.76465C3.74082 4.7502 4.4137 3.33897 4.44336 3.27734C4.57108 3.01227 4.87828 2.88517 5.15625 2.98242C5.22027 3.00485 6.69739 3.52792 8.43262 4.64648C9.45562 5.30583 10.3576 6.03768 11.1133 6.82129C11.4357 7.15573 11.7311 7.50055 12 7.85352C13.2521 6.21362 14.8899 5.03725 16.1104 4.31055C17.6051 3.42074 18.792 3.00044 18.8438 2.98242ZM5.27344 4.27344C5.04824 4.83307 4.69114 5.82402 4.43262 7.0332C4.06592 8.74827 3.82917 11.2646 4.77148 13.5342C5.71367 15.8033 7.66398 17.4151 9.13867 18.3691C9.7898 18.7902 10.4086 19.1226 10.916 19.3711L6.26855 8.17676C6.14615 7.88198 6.28693 7.54391 6.58203 7.42188C6.87707 7.29987 7.21563 7.43986 7.33789 7.73438L11.9854 18.9297C12.6181 17.0782 13.4735 13.5472 12.1943 10.4658C12.0063 10.0131 11.7787 9.58581 11.5215 9.18359V9.18457C11.5157 9.1761 11.5103 9.16715 11.5049 9.1582L11.3301 8.89746C9.51064 6.27627 6.46907 4.7841 5.27344 4.27344ZM18.7246 4.27246C18.2607 4.47002 17.5187 4.81509 16.6826 5.31445C15.4783 6.03377 13.84 7.22122 12.6855 8.87305C12.9071 9.24935 13.1003 9.63335 13.2627 10.0244C13.773 11.2536 14.0207 12.6229 14.0127 14.1143L17.2002 6.4375C17.3224 6.14303 17.661 6.00342 17.9561 6.125C18.2512 6.2469 18.3919 6.58524 18.2695 6.87988L13.083 19.3711C13.5907 19.1226 14.2098 18.7906 14.8613 18.3691C16.336 17.4152 18.2863 15.8033 19.2285 13.5342C20.1708 11.2646 19.9341 8.74827 19.5674 7.0332C19.3085 5.82292 18.9498 4.83177 18.7246 4.27246Z"
        fill="currentColor"
        stroke="currentColor"
        strokeWidth="0.1"
      />
    </svg>
  )
}

/**
 * Supplements icon — pill/capsule shape. Used in recommendation pills.
 * Source: design-system/components/ui/icons/supplements.tsx
 */
export function SupplementsIcon({
  className,
  size = 24,
}: {
  className?: string
  size?: number
}) {
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M12.6845 4.42452L4.42342 12.6856C2.52553 14.5835 2.52553 17.6764 4.42342 19.5743C5.33767 20.4863 6.57635 20.9985 7.86775 20.9985C9.15914 20.9985 10.3978 20.4863 11.3121 19.5743L19.5728 11.3128C20.0253 10.8606 20.3842 10.3236 20.6291 9.73264C20.874 9.14165 21 8.50821 21 7.86849C21 7.22878 20.874 6.59533 20.6291 6.00435C20.3842 5.41336 20.0253 4.87641 19.5728 4.42417C17.6739 2.52522 14.5838 2.52522 12.6845 4.42452ZM10.3673 18.6299C9.70375 19.2917 8.80479 19.6634 7.86757 19.6634C6.93036 19.6634 6.03139 19.2917 5.3678 18.6299C4.70485 17.9669 4.33242 17.0677 4.33242 16.1301C4.33242 15.1925 4.70485 14.2934 5.3678 13.6304L9.37833 9.61948C10.5358 10.02 11.5871 10.6781 12.4532 11.5442C13.3192 12.4102 13.9773 13.4616 14.3779 14.619L10.3673 18.6299ZM18.6285 10.3684L15.4112 13.5854C14.457 11.333 12.6641 9.54018 10.4116 8.58618L13.6289 5.3689C13.9561 5.03577 14.3461 4.77075 14.7762 4.58916C15.2064 4.40756 15.6683 4.31299 16.1353 4.31089C16.6022 4.30879 17.0649 4.39921 17.4967 4.57694C17.9285 4.75466 18.3208 5.01616 18.651 5.34633C18.9812 5.67651 19.2427 6.06883 19.4204 6.50063C19.5981 6.93243 19.6886 7.39515 19.6865 7.86209C19.6844 8.32903 19.5898 8.79092 19.4082 9.22111C19.2266 9.65129 18.9616 10.0412 18.6285 10.3684Z"
        fill="currentColor"
      />
    </svg>
  )
}

/**
 * Action plan icon — target with arrow + sparkle. Suggests "personalized plan/insight".
 * Source: design-system/components/ui/icons/action-plan.tsx
 */
export function ActionPlanIcon({
  className,
  size = 24,
}: {
  className?: string
  size?: number
}) {
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M11.9922 18.1055C8.63207 18.1055 5.89844 15.3718 5.89844 12.0117C5.89844 8.6516 8.63207 5.91797 11.9922 5.91797C15.3523 5.91797 18.0859 8.6516 18.0859 12.0117C18.0859 15.3718 15.3523 18.1055 11.9922 18.1055Z"
        stroke="currentColor"
        strokeMiterlimit="10"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M11.9922 14.5898C10.5706 14.5898 9.41406 13.4333 9.41406 12.0117C9.41406 10.5901 10.5706 9.43359 11.9922 9.43359C13.4138 9.43359 14.5703 10.5901 14.5703 12.0117C14.5703 13.4333 13.4138 14.5898 11.9922 14.5898Z"
        stroke="currentColor"
        strokeMiterlimit="10"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M11.9922 12.0117L16.3011 7.70277"
        stroke="currentColor"
        strokeMiterlimit="10"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M21.0812 5.40863L18.5953 7.89453L16.3027 7.70117L16.1094 5.40863L18.5953 2.9227L18.7886 5.21527L21.0812 5.40863Z"
        stroke="currentColor"
        strokeMiterlimit="10"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M19.7752 6.71465L18.5937 7.89613L16.3011 7.70281L16.1078 5.41024L17.2893 4.22871C15.7802 3.19965 13.9566 2.59766 11.9922 2.59766C6.79293 2.59766 2.57812 6.8125 2.57812 12.0117C2.57812 17.211 6.79293 21.4258 11.9922 21.4258C17.1914 21.4258 21.4062 17.211 21.4062 12.0117C21.4062 10.0473 20.8043 8.22375 19.7752 6.71465Z"
        stroke="currentColor"
        strokeMiterlimit="10"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

/**
 * Account (filled person silhouette), 24x24. DS canonical.
 * Source: design-system/components/ui/icons/account.tsx
 */
export function AccountIcon({
  className,
  size = 24,
}: {
  className?: string
  size?: number
}) {
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M11.8702 11.6344C13.2186 11.6344 14.3861 11.1597 15.3403 10.2231C16.2942 9.28661 16.778 8.14079 16.778 6.81705C16.778 5.49376 16.2944 4.34779 15.3402 3.41103C14.386 2.47472 13.2185 2 11.8702 2C10.5216 2 9.35425 2.47472 8.4002 3.41118C7.44616 4.34764 6.96237 5.49361 6.96237 6.81705C6.96237 8.14079 7.44616 9.28676 8.4002 10.2232C9.35456 11.1595 10.5221 11.6344 11.8702 11.6344ZM9.24466 4.23991C9.97671 3.52135 10.8355 3.17207 11.8702 3.17207C12.9048 3.17207 13.7637 3.52135 14.4959 4.23991C15.2279 4.95862 15.5839 5.8017 15.5839 6.81705C15.5839 7.83271 15.2279 8.67563 14.4959 9.39434C13.7637 10.1131 12.9048 10.4623 11.8702 10.4623C10.8358 10.4623 9.97702 10.1129 9.24466 9.39434C8.51245 8.67578 8.15645 7.83271 8.15645 6.81705C8.15645 5.8017 8.51245 4.95862 9.24466 4.23991Z"
        fill="currentColor"
      />
      <path
        d="M20.4588 17.3789C20.4313 16.9892 20.3756 16.564 20.2937 16.1151C20.211 15.6628 20.1045 15.2353 19.977 14.8445C19.8452 14.4406 19.6663 14.0417 19.4447 13.6594C19.2151 13.2627 18.9453 12.9172 18.6424 12.6329C18.3258 12.3355 17.938 12.0964 17.4897 11.922C17.0429 11.7485 16.5478 11.6606 16.0181 11.6606C15.8101 11.6606 15.609 11.7444 15.2205 11.9927C14.9814 12.1457 14.7017 12.3227 14.3896 12.5185C14.1226 12.6854 13.761 12.8418 13.3144 12.9834C12.8787 13.1218 12.4362 13.192 11.9994 13.192C11.5629 13.192 11.1204 13.1218 10.6844 12.9834C10.2382 12.842 9.87644 12.6856 9.60998 12.5186C9.30078 12.3247 9.02095 12.1477 8.77828 11.9925C8.3901 11.7442 8.18894 11.6605 7.98093 11.6605C7.45113 11.6605 6.95615 11.7485 6.50952 11.9222C6.06149 12.0963 5.67362 12.3354 5.35664 12.6331C5.05381 12.9175 4.78393 13.2628 4.55447 13.6594C4.33326 14.0417 4.15417 14.4404 4.02234 14.8446C3.89502 15.2354 3.78853 15.6628 3.70583 16.1151C3.62374 16.5634 3.56825 16.9887 3.54073 17.3793C3.51368 17.7613 3.5 18.1588 3.5 18.5604C3.5 19.6044 3.83812 20.4497 4.50488 21.073C5.16341 21.6881 6.03459 22 7.09435 22H16.9056C17.9651 22 18.8363 21.6881 19.495 21.073C20.1619 20.4501 20.5 19.6046 20.5 18.5603C20.4998 18.1573 20.486 17.7598 20.4588 17.3789ZM18.6717 20.2238C18.2365 20.6303 17.6588 20.8279 16.9055 20.8279H7.09435C6.34085 20.8279 5.76316 20.6303 5.32819 20.224C4.90146 19.8252 4.69408 19.281 4.69408 18.5604C4.69408 18.1856 4.70667 17.8156 4.73185 17.4604C4.75641 17.1118 4.80663 16.729 4.88109 16.3222C4.95462 15.9204 5.04821 15.5433 5.15952 15.202C5.26632 14.8747 5.41198 14.5506 5.59263 14.2384C5.76503 13.9408 5.96339 13.6855 6.18228 13.4798C6.38702 13.2874 6.64508 13.1299 6.94916 13.0118C7.23038 12.9026 7.54643 12.8428 7.88952 12.8338C7.93134 12.8556 8.00581 12.8972 8.12644 12.9744C8.37191 13.1315 8.65485 13.3106 8.96763 13.5067C9.32021 13.7273 9.77446 13.9266 10.3172 14.0986C10.872 14.2747 11.4379 14.3641 11.9995 14.3641C12.5612 14.3641 13.1272 14.2747 13.6817 14.0987C14.2249 13.9265 14.679 13.7273 15.0321 13.5064C15.3521 13.3056 15.6272 13.1316 15.8726 12.9744C15.9933 12.8974 16.0677 12.8556 16.1095 12.8338C16.4528 12.8428 16.7688 12.9026 17.0502 13.0118C17.3541 13.1299 17.6122 13.2876 17.8169 13.4798C18.0358 13.6854 18.2342 13.9407 18.4066 14.2385C18.5874 14.5506 18.7332 14.8748 18.8399 15.2018C18.9513 15.5436 19.0451 15.9206 19.1184 16.322C19.1927 16.7296 19.2431 17.1126 19.2677 17.4605C19.293 17.8144 19.3058 18.1846 19.3059 18.5604C19.3058 19.2811 19.0984 19.8252 18.6717 20.2238Z"
        fill="currentColor"
      />
    </svg>
  )
}

/**
 * Contrast (medicine vial), stroked 24x24. DS canonical.
 * Source: design-system/components/ui/icons/contrast.tsx
 */
export function ContrastIcon({
  className,
  size = 24,
}: {
  className?: string
  size?: number
}) {
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M14.739 6.913V5.348H9.26V6.913L7.888 8.091C7.45945 8.45819 7.11539 8.91374 6.87944 9.42638C6.64348 9.93903 6.5212 10.4967 6.521 11.061V20.688C6.521 21.336 7.047 21.862 7.695 21.862H16.305C16.953 21.862 17.478 21.336 17.478 20.688V11.061C17.478 9.918 16.978 8.833 16.112 8.09L14.739 6.913ZM14.74 3.001H9.26C9.05268 3.00126 8.85393 3.08374 8.70733 3.23033C8.56073 3.37693 8.47826 3.57568 8.478 3.783V5.348H15.521V3.783C15.521 3.57534 15.4385 3.37618 15.2917 3.22934C15.1448 3.08249 14.9457 3 14.738 3L14.74 3.001Z"
        stroke="currentColor"
        strokeWidth="1.174"
        strokeMiterlimit="10"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M15.13 19.435H6.521V12.392H15.131L15.13 19.435Z"
        stroke="currentColor"
        strokeWidth="1.174"
        strokeMiterlimit="10"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

/**
 * Heart outline (filled with empty center), 24x24. DS canonical.
 * Source: design-system/components/ui/icons/favorites-outlined.tsx
 */
export function FavoritesOutlinedIcon({
  className,
  size = 24,
}: {
  className?: string
  size?: number
}) {
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M16.8545 3.59338C18.3408 3.59343 19.6916 4.16861 20.6748 5.20374C21.648 6.2285 22.2002 7.6454 22.2002 9.21155C22.2002 10.7806 21.609 12.2565 20.21 13.8951C18.9333 15.3907 17.0811 16.9257 14.8467 18.774V18.775C14.09 19.4011 13.2272 20.1137 12.332 20.8737V20.8746C12.244 20.9493 12.1262 20.9938 12 20.9938C11.8738 20.9938 11.7564 20.9489 11.668 20.8737H11.667C10.7719 20.114 9.91069 19.4008 9.1543 18.775C6.9194 16.9262 5.06683 15.3906 3.79004 13.8951C2.39104 12.2563 1.7998 10.7803 1.7998 9.21155C1.79984 7.74338 2.28525 6.40669 3.14746 5.401L3.3252 5.20374C4.30835 4.16868 5.65925 3.59343 7.14551 3.59338C8.24067 3.59338 9.23572 3.92601 10.1221 4.59143C10.5735 4.93027 10.9884 5.34959 11.3584 5.84631L12 6.70764L12.6416 5.84631C13.0118 5.3494 13.4267 4.93029 13.8779 4.59143C14.7644 3.92604 15.7594 3.59338 16.8545 3.59338Z"
        stroke="currentColor"
        strokeWidth="1.6"
      />
    </svg>
  )
}

/**
 * Pacemaker icon (Material Symbols), 24x24. Used in ProductInfoModal "Key benefits" tile.
 * Source: Figma Booking-flow 611:4863.
 */
export function PacemakerIcon({
  className,
  size = 24,
}: {
  className?: string
  size?: number
}) {
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M6.58425 21.3943C5.71525 21.3943 4.97433 21.0873 4.3615 20.4735C3.74867 19.8595 3.44225 19.1183 3.44225 18.25V10.4693C3.02442 10.0679 2.58667 9.64967 2.129 9.2145C1.67117 8.77917 1.44225 8.25767 1.44225 7.65C1.44225 7.26417 1.57917 6.93433 1.853 6.6605C2.12667 6.38683 2.45642 6.25 2.84225 6.25C3.11375 6.25 3.364 6.32117 3.593 6.4635C3.822 6.60583 4.02175 6.78467 4.19225 7C4.36275 6.78467 4.56258 6.60583 4.79175 6.4635C5.02075 6.32117 5.27092 6.25 5.54225 6.25C5.92825 6.25 6.25808 6.38683 6.53175 6.6605C6.80542 6.93433 6.94225 7.26417 6.94225 7.65C6.94225 8.25767 6.71342 8.77917 6.25575 9.2145C5.79808 9.64967 5.36025 10.0679 4.94225 10.4693V18.25C4.94225 18.7067 5.102 19.0949 5.4215 19.4148C5.74083 19.7344 6.12858 19.8943 6.58475 19.8943C7.04108 19.8943 7.4295 19.7344 7.75 19.4148C8.0705 19.0949 8.23075 18.7067 8.23075 18.25V7.25C8.23075 5.9465 8.69017 4.83533 9.609 3.9165C10.5277 2.99783 11.6387 2.5385 12.9422 2.5385C14.2457 2.5385 15.3569 2.99783 16.2758 3.9165C17.1944 4.83533 17.6537 5.9465 17.6537 7.25V7.57125C18.7269 7.74692 19.6202 8.24883 20.3335 9.077C21.047 9.90517 21.4037 10.8795 21.4037 12V17C21.4037 18.2487 20.9659 19.3108 20.0903 20.1865C19.2148 21.0622 18.1526 21.5 16.9038 21.5C15.6551 21.5 14.5929 21.0622 13.7173 20.1865C12.8418 19.3108 12.404 18.2487 12.404 17V12C12.404 10.8795 12.7607 9.90517 13.474 9.077C14.1875 8.24883 15.0808 7.74692 16.1538 7.57125V7.25C16.1538 6.35783 15.8417 5.59958 15.2175 4.97525C14.5932 4.35075 13.8352 4.0385 12.9435 4.0385C12.0517 4.0385 11.2933 4.35075 10.6683 4.97525C10.0433 5.59958 9.73075 6.35783 9.73075 7.25V18.25C9.73075 19.1183 9.42358 19.8595 8.80925 20.4735C8.19492 21.0873 7.45325 21.3943 6.58425 21.3943ZM16.9038 20C17.7371 20 18.4454 19.7083 19.0287 19.125C19.6121 18.5417 19.9037 17.8333 19.9037 17V12C19.9037 11.1667 19.6121 10.4583 19.0287 9.875C18.4454 9.29167 17.7371 9 16.9038 9C16.0704 9 15.3621 9.29167 14.7788 9.875C14.1954 10.4583 13.9038 11.1667 13.9038 12V17C13.9038 17.8333 14.1954 18.5417 14.7788 19.125C15.3621 19.7083 16.0704 20 16.9038 20ZM16.9038 15.25C16.4179 15.25 16.0048 15.4202 15.6645 15.7605C15.324 16.101 15.1538 16.5142 15.1538 17C15.1538 17.4858 15.324 17.899 15.6645 18.2395C16.0048 18.5798 16.4179 18.75 16.9038 18.75C17.3898 18.75 17.8029 18.5798 18.1432 18.2395C18.4836 17.899 18.6537 17.4858 18.6537 17C18.6537 16.5142 18.4836 16.101 18.1432 15.7605C17.8029 15.4202 17.3898 15.25 16.9038 15.25Z"
        fill="currentColor"
      />
    </svg>
  )
}

/**
 * Person-heart icon (Material Symbols), 24x24. Used in ProductInfoModal "Who is it for" tile.
 * Source: Figma Booking-flow 611:4877.
 */
export function PersonHeartIcon({
  className,
  size = 24,
}: {
  className?: string
  size?: number
}) {
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M4.5 19.3078V17.0845C4.5 16.5948 4.633 16.1413 4.899 15.724C5.165 15.3067 5.5205 14.9858 5.9655 14.7615C6.93467 14.2897 7.91092 13.9327 8.89425 13.6905C9.87758 13.4482 10.8712 13.3206 11.875 13.3078C11.9097 13.3398 11.9423 13.3686 11.973 13.3942C12.0038 13.4199 12.0398 13.4488 12.0808 13.4808C11.9679 13.6811 11.8833 13.8952 11.827 14.1232C11.7705 14.3514 11.7422 14.5796 11.7422 14.8078C10.8679 14.8308 10.0042 14.9499 9.151 15.1652C8.29783 15.3807 7.46542 15.6923 6.65375 16.1C6.45258 16.209 6.29333 16.3497 6.176 16.522C6.05867 16.6945 6 16.882 6 17.0845V17.8078H13.123L14.623 19.3078H4.5ZM12 11.6922C11.0375 11.6922 10.2136 11.3496 9.52825 10.6642C8.84275 9.97875 8.5 9.15475 8.5 8.19225C8.5 7.22975 8.84275 6.40583 9.52825 5.7205C10.2136 5.035 11.0375 4.69225 12 4.69225C12.9625 4.69225 13.7864 5.035 14.4718 5.7205C15.1573 6.40583 15.5 7.22975 15.5 8.19225C15.5 9.15475 15.1573 9.97875 14.4718 10.6642C13.7864 11.3496 12.9625 11.6922 12 11.6922ZM12 10.1922C12.55 10.1922 13.0208 9.99642 13.4125 9.60475C13.8042 9.21308 14 8.74225 14 8.19225C14 7.64225 13.8042 7.17142 13.4125 6.77975C13.0208 6.38808 12.55 6.19225 12 6.19225C11.45 6.19225 10.9792 6.38808 10.5875 6.77975C10.1958 7.17142 10 7.64225 10 8.19225C10 8.74225 10.1958 9.21308 10.5875 9.60475C10.9792 9.99642 11.45 10.1922 12 10.1922ZM17.7172 19.3078L14.4865 16.077C14.2827 15.873 14.1397 15.6567 14.0577 15.4282C13.9757 15.1996 13.9348 14.9709 13.9348 14.7422C13.9348 14.2489 14.1111 13.8096 14.4637 13.4243C14.8166 13.0389 15.2691 12.8463 15.8212 12.8463C16.2549 12.8463 16.5957 12.9465 16.8435 13.147C17.0913 13.3477 17.3826 13.6153 17.7172 13.95C18.0314 13.6358 18.3153 13.3733 18.569 13.1625C18.8225 12.9517 19.1722 12.8463 19.618 12.8463C20.1867 12.8463 20.6425 13.0411 20.9855 13.4308C21.3285 13.8204 21.5 14.262 21.5 14.7555C21.5 14.9867 21.4548 15.2179 21.3645 15.4492C21.274 15.6804 21.1352 15.8897 20.948 16.077L17.7172 19.3078Z"
        fill="currentColor"
      />
    </svg>
  )
}

/**
 * Info-row icon set for ProductInfoModal (Figma Booking-flow 569:4959).
 * Visually cohesive 6-icon family: filled for tube/clock/prep/user, stroked for rad/contrast.
 * These intentionally diverge from DS-canonical RadiationIcon/ContrastIcon/etc. because the
 * canonical versions have minor offsets (e.g. radiation `cy=12.4` vs Figma's `cy=12`) that
 * show as asymmetric cropping at small sizes.
 */
export function InfoHealthAreasIcon({
  className,
  size = 24,
}: {
  className?: string
  size?: number
}) {
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="9 1 7 22"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M15.523 3.644H15.393V19.875C15.393 21.598 14.095 23 12.5 23C10.905 23 9.607 21.598 9.607 19.875V3.645H9.477C9.214 3.645 9 3.414 9 3.129V1.516C9 1.23 9.214 1 9.477 1H15.523C15.786 1 16 1.23 16 1.516V3.129C16 3.414 15.786 3.644 15.523 3.644ZM14.438 3.644H10.561V11.351C12.117 11.061 12.793 11.636 13.086 12.208C13.303 12.631 13.757 12.798 14.438 12.707V3.644ZM10.561 12.401V19.875C10.561 21.03 11.431 21.969 12.5 21.969C13.569 21.969 14.438 21.029 14.438 19.875V13.745C13.315 13.86 12.616 13.42 12.25 12.708C11.99 12.201 11.187 12.262 10.562 12.401H10.561ZM9.955 2.613H15.045V2.031H9.955V2.613Z"
        fill="currentColor"
      />
    </svg>
  )
}

export function InfoRadiationIcon({
  className,
  size = 24,
}: {
  className?: string
  size?: number
}) {
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="3 3 18 18"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M11.24 13.314L9.708 15.97C9.42 16.468 8.775 16.645 8.338 16.27C7.36475 15.4366 6.70565 14.2955 6.47 13.036C6.365 12.47 6.84 12 7.415 12H10.518M11.25 10.699L9.706 8.028C9.419 7.531 9.587 6.884 10.129 6.693C10.7301 6.48157 11.3628 6.37403 12 6.375C12.656 6.375 13.285 6.487 13.87 6.693C14.412 6.884 14.58 7.53 14.293 8.028L12.777 10.654M13.58 12L17.625 11.999C17.626 12.8104 17.4511 13.6123 17.1122 14.3495C16.7733 15.0866 16.2785 15.7415 15.662 16.269C15.226 16.644 14.581 16.467 14.293 15.969L12.773 13.337"
        stroke="currentColor"
        strokeMiterlimit="10"
      />
      <path
        d="M12 13.405C12.3729 13.405 12.7305 13.2569 12.9942 12.9932C13.2579 12.7295 13.406 12.3719 13.406 11.999C13.406 11.6261 13.2579 11.2685 12.9942 11.0048C12.7305 10.7411 12.3729 10.593 12 10.593C11.627 10.593 11.2692 10.7412 11.0055 11.005C10.7417 11.2687 10.5935 11.6265 10.5935 11.9995C10.5935 12.3725 10.7417 12.7303 11.0055 12.994C11.2692 13.2578 11.627 13.406 12 13.406V13.405Z"
        stroke="currentColor"
        strokeMiterlimit="10"
      />
      <path
        d="M12 20.4C16.6392 20.4 20.4 16.6392 20.4 12C20.4 7.36081 16.6392 3.6 12 3.6C7.36081 3.6 3.6 7.36081 3.6 12C3.6 16.6392 7.36081 20.4 12 20.4Z"
        stroke="currentColor"
        strokeWidth="1.2"
      />
    </svg>
  )
}

export function InfoTimeIcon({
  className,
  size = 24,
}: {
  className?: string
  size?: number
}) {
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="4 4 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M14.852 13.413L12.62 11.74V8.33C12.62 8.16557 12.5547 8.00787 12.4384 7.89159C12.3221 7.77532 12.1644 7.71 12 7.71C11.8356 7.71 11.6779 7.77532 11.5616 7.89159C11.4453 8.00787 11.38 8.16557 11.38 8.33V12.049C11.38 12.244 11.472 12.428 11.628 12.544L14.108 14.404C14.173 14.4532 14.247 14.4889 14.3259 14.5093C14.4048 14.5296 14.487 14.5341 14.5676 14.5225C14.6482 14.5108 14.7258 14.4833 14.7957 14.4415C14.8656 14.3998 14.9266 14.3445 14.975 14.279C15.0238 14.2139 15.0593 14.1399 15.0795 14.0611C15.0997 13.9823 15.1042 13.9003 15.0927 13.8198C15.0812 13.7393 15.0539 13.6618 15.0124 13.5918C14.971 13.5219 14.9161 13.4608 14.851 13.412L14.852 13.413Z"
        fill="currentColor"
      />
      <path
        d="M12 4C7.589 4 4 7.589 4 12C4 16.412 7.589 20 12 20C16.412 20 20 16.412 20 12C20 7.589 16.412 4 12 4ZM12 18.76C10.2079 18.7576 8.48982 18.0446 7.22259 16.7774C5.95536 15.5102 5.24238 13.7921 5.24 12C5.24238 10.2079 5.95536 8.48982 7.22259 7.22259C8.48982 5.95536 10.2079 5.24238 12 5.24C13.7921 5.24238 15.5102 5.95536 16.7774 7.22259C18.0446 8.48982 18.7576 10.2079 18.76 12C18.7574 13.7921 18.0443 15.5099 16.7771 16.7771C15.5099 18.0443 13.7921 18.7574 12 18.76Z"
        fill="currentColor"
      />
    </svg>
  )
}

export function InfoContrastIcon({
  className,
  size = 24,
}: {
  className?: string
  size?: number
}) {
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="5.9 1.5 12.2 20.1"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M14.739 6.052V4.487H9.26V6.052L7.888 7.23C7.45945 7.59719 7.1154 8.05274 6.87944 8.56538C6.64348 9.07803 6.52121 9.63566 6.521 10.2V19.827C6.521 20.475 7.047 21.001 7.695 21.001H16.305C16.953 21.001 17.478 20.475 17.478 19.827V10.2C17.478 9.057 16.978 7.972 16.112 7.229L14.739 6.052ZM14.74 2.14H9.26C9.05268 2.14026 8.85393 2.22274 8.70733 2.36933C8.56074 2.51593 8.47826 2.71468 8.478 2.922V4.487H15.521V2.922C15.521 2.71434 15.4385 2.51518 15.2917 2.36834C15.1448 2.22149 14.9457 2.139 14.738 2.139L14.74 2.14Z"
        stroke="currentColor"
        strokeWidth="1.174"
        strokeMiterlimit="10"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M15.13 18.574H6.521V11.531H15.131L15.13 18.574Z"
        stroke="currentColor"
        strokeWidth="1.174"
        strokeMiterlimit="10"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

export function InfoPreparationIcon({
  className,
  size = 24,
}: {
  className?: string
  size?: number
}) {
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="3.9 3.9 16.2 16.2"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M16.162 10.178H12.235C12.0865 10.177 11.9445 10.1175 11.8395 10.0125C11.7345 9.90755 11.675 9.76546 11.674 9.617C11.674 9.31 11.928 9.056 12.234 9.056H16.162C16.3108 9.056 16.4535 9.11511 16.5587 9.22031C16.6639 9.32552 16.723 9.46821 16.723 9.617C16.723 9.76579 16.6639 9.90848 16.5587 10.0137C16.4535 10.1189 16.3108 10.178 16.162 10.178ZM8.308 10.746C8.23425 10.7464 8.16116 10.7321 8.09299 10.704C8.02483 10.6758 7.96296 10.6343 7.911 10.582L7.351 10.021C7.24663 9.91542 7.1881 9.77295 7.1881 9.6245C7.1881 9.47605 7.24663 9.33358 7.351 9.228C7.45653 9.12394 7.59879 9.0656 7.747 9.0656C7.89521 9.0656 8.03747 9.12394 8.143 9.228L8.308 9.392L9.594 8.106C9.69958 8.00163 9.84205 7.9431 9.9905 7.9431C10.139 7.9431 10.2814 8.00163 10.387 8.106C10.4914 8.21158 10.5499 8.35405 10.5499 8.5025C10.5499 8.65095 10.4914 8.79342 10.387 8.899L8.704 10.582C8.652 10.634 8.59026 10.6752 8.52231 10.7034C8.45437 10.7315 8.38154 10.746 8.308 10.746ZM16.162 15.415H12.235C12.0865 15.414 11.9445 15.3545 11.8395 15.2495C11.7345 15.1445 11.675 15.0025 11.674 14.854C11.674 14.547 11.928 14.293 12.234 14.293H16.162C16.3108 14.293 16.4535 14.3521 16.5587 14.4573C16.6639 14.5625 16.723 14.7052 16.723 14.854C16.723 15.0028 16.6639 15.1455 16.5587 15.2507C16.4535 15.3559 16.3108 15.415 16.162 15.415ZM8.308 15.983C8.23418 15.9833 8.16104 15.9688 8.09287 15.9405C8.02471 15.9122 7.96287 15.8705 7.911 15.818L7.351 15.258C7.24663 15.1524 7.1881 15.01 7.1881 14.8615C7.1881 14.713 7.24663 14.5706 7.351 14.465C7.45653 14.3609 7.59879 14.3026 7.747 14.3026C7.89521 14.3026 8.03747 14.3609 8.143 14.465L8.308 14.629L9.594 13.342C9.69958 13.2376 9.84205 13.1791 9.9905 13.1791C10.139 13.1791 10.2814 13.2376 10.387 13.342C10.4914 13.4476 10.5499 13.59 10.5499 13.7385C10.5499 13.887 10.4914 14.0294 10.387 14.135L8.704 15.82C8.652 15.872 8.59026 15.9132 8.52231 15.9414C8.45437 15.9695 8.38154 15.984 8.308 15.984V15.983Z"
        fill="currentColor"
      />
      <path
        d="M14.202 20H9.713C5.652 20 3.916 18.264 3.916 14.202V9.714C3.916 5.652 5.652 3.916 9.713 3.916H14.202C18.264 3.916 19.999 5.652 19.999 9.714V14.202C20 18.264 18.264 20 14.202 20ZM9.713 5.039C6.265 5.039 5.038 6.265 5.038 9.714V14.202C5.038 17.651 6.265 18.878 9.713 18.878H14.202C17.65 18.878 18.877 17.651 18.877 14.202V9.714C18.877 6.265 17.65 5.039 14.202 5.039H9.713Z"
        fill="currentColor"
      />
    </svg>
  )
}

export function InfoForWhomIcon({
  className,
  size = 24,
}: {
  className?: string
  size?: number
}) {
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="3 3 18 18"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M18.364 5.636C17.5302 4.79765 16.5384 4.133 15.446 3.68053C14.3537 3.22806 13.1824 2.99676 12 3C10.8176 2.99676 9.64634 3.22806 8.55396 3.68053C7.46158 4.133 6.46979 4.79765 5.636 5.636C4.79765 6.46979 4.133 7.46158 3.68053 8.55396C3.22806 9.64634 2.99676 10.8176 3 12C2.9967 13.1824 3.22798 14.3537 3.68046 15.4461C4.13293 16.5385 4.79761 17.5302 5.636 18.364C6.46979 19.2023 7.46158 19.867 8.55396 20.3195C9.64634 20.7719 10.8176 21.0032 12 21C13.1824 21.0032 14.3537 20.7719 15.446 20.3195C16.5384 19.867 17.5302 19.2023 18.364 18.364C19.2023 17.5302 19.867 16.5384 20.3195 15.446C20.7719 14.3537 21.0032 13.1824 21 12C21.0032 10.8176 20.7719 9.64634 20.3195 8.55396C19.867 7.46158 19.2023 6.46979 18.364 5.636ZM12 19.946C9.94239 19.9472 7.96511 19.1474 6.487 17.716C6.8692 16.5552 7.60771 15.5444 8.59749 14.8276C9.58726 14.1107 10.7779 13.7242 12 13.723C13.2221 13.7242 14.4127 14.1107 15.4025 14.8276C16.3923 15.5444 17.1308 16.5552 17.513 17.716C16.0349 19.1474 14.0576 19.9472 12 19.946ZM9.363 10.03C9.36432 9.33121 9.64264 8.66146 10.1369 8.16753C10.6312 7.6736 11.3012 7.39579 12 7.395C12.699 7.39579 13.3691 7.67374 13.8634 8.16788C14.3577 8.66203 14.6359 9.33204 14.637 10.031C14.6362 10.7301 14.3581 11.4004 13.8638 11.8948C13.3694 12.3891 12.6991 12.6672 12 12.668C11.3009 12.6672 10.6306 12.3891 10.1362 11.8948C9.64187 11.4004 9.36379 10.7301 9.363 10.031V10.03ZM18.305 16.828C17.9226 15.9369 17.3561 15.1367 16.6426 14.4801C15.9291 13.8235 15.0847 13.3252 14.165 13.018C14.797 12.5609 15.2678 11.9152 15.5098 11.1738C15.7518 10.4323 15.7526 9.63326 15.5121 8.89132C15.2716 8.14939 14.8021 7.50278 14.1711 7.04438C13.54 6.58599 12.7799 6.33938 12 6.34C11.2196 6.33901 10.459 6.58553 9.82756 7.0441C9.19612 7.50268 8.72637 8.1497 8.48587 8.89211C8.24536 9.63451 8.24649 10.4341 8.48909 11.1758C8.73169 11.9175 9.20327 12.5632 9.836 13.02C8.91627 13.3272 8.07192 13.8255 7.35841 14.4821C6.64489 15.1387 6.07836 15.9389 5.696 16.83C4.62993 15.4457 4.05251 13.7472 4.054 12C4.054 7.619 7.618 4.055 11.999 4.055C16.38 4.055 19.945 7.619 19.945 12C19.945 13.815 19.333 15.49 18.305 16.829V16.828Z"
        fill="currentColor"
      />
    </svg>
  )
}

/**
 * Translate badge — Material Design filled "translate" glyph (the
 * familiar 文A symbol). Used to mark customer reviews that have been
 * translated from their original language.
 */
export function TranslateIcon({
  width = 14,
  height = 14,
  className,
}: IconProps) {
  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
      className={className}
    >
      <path d="m12.87 15.07-2.54-2.51.03-.03A17.52 17.52 0 0 0 14.07 6H17V4h-7V2H8v2H1v2h11.17C11.5 7.92 10.44 9.75 9 11.35 8.07 10.32 7.3 9.19 6.69 8h-2c.73 1.63 1.73 3.17 2.98 4.56l-5.09 5.02L4 19l5-5 3.11 3.11.76-2.04zM18.5 10h-2L12 22h2l1.12-3h4.75L21 22h2l-4.5-12zm-2.62 7 1.62-4.33L19.12 17h-3.24z" />
    </svg>
  )
}

export function LinkedInIcon({
  width = 20,
  height = 20,
  className,
}: IconProps) {
  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
      className={className}
    >
      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
    </svg>
  )
}

/** Copy / duplicate — stroke, 24x24 viewBox (Lucide "copy"). Copy-to-clipboard affordances. */
export function CopyIcon({
  width = 18,
  height = 18,
  className,
  ...props
}: IconProps) {
  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={className}
      {...props}
    >
      <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </svg>
  )
}

/** Download — stroke, 24x24 viewBox (Lucide "download"). File-save affordances. */
export function DownloadIcon({
  width = 18,
  height = 18,
  className,
  ...props
}: IconProps) {
  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={className}
      {...props}
    >
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  )
}

/**
 * Virus glyph (filled). Used for the "Diseases" tab in the
 * biomarker/disease explorer switcher. Geometry ported from the Paper design.
 */
export function VirusIcon({ width = 24, height = 24, className }: IconProps) {
  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
      className={className}
    >
      <path
        transform="translate(0.75 0.75)"
        d="M12.000 9.375C12.000 8.856 11.846 8.348 11.558 7.917C11.269 7.485 10.859 7.148 10.380 6.950C9.900 6.751 9.372 6.699 8.863 6.800C8.354 6.902 7.886 7.152 7.519 7.519C7.152 7.886 6.902 8.354 6.800 8.863C6.699 9.372 6.751 9.900 6.950 10.380C7.148 10.859 7.485 11.269 7.917 11.558C8.348 11.846 8.856 12.000 9.375 12.000C10.071 12.000 10.739 11.723 11.231 11.231C11.723 10.739 12.000 10.071 12.000 9.375ZM9.375 10.500C9.152 10.500 8.935 10.434 8.750 10.310C8.565 10.187 8.421 10.011 8.336 9.806C8.250 9.600 8.228 9.374 8.272 9.156C8.315 8.937 8.422 8.737 8.580 8.580C8.737 8.422 8.937 8.315 9.156 8.272C9.374 8.228 9.600 8.250 9.806 8.336C10.011 8.421 10.187 8.565 10.310 8.750C10.434 8.935 10.500 9.152 10.500 9.375C10.500 9.673 10.381 9.960 10.170 10.170C9.960 10.381 9.673 10.500 9.375 10.500ZM15.750 9.750C16.047 9.750 16.337 9.838 16.583 10.003C16.830 10.168 17.022 10.402 17.136 10.676C17.249 10.950 17.279 11.252 17.221 11.543C17.163 11.834 17.020 12.101 16.811 12.311C16.601 12.520 16.334 12.663 16.043 12.721C15.752 12.779 15.450 12.749 15.176 12.636C14.902 12.522 14.668 12.330 14.503 12.083C14.338 11.837 14.250 11.547 14.250 11.250C14.250 10.852 14.408 10.471 14.689 10.189C14.971 9.908 15.352 9.750 15.750 9.750ZM12.750 15.750C12.750 16.047 12.662 16.337 12.497 16.583C12.332 16.830 12.098 17.022 11.824 17.136C11.550 17.249 11.248 17.279 10.957 17.221C10.666 17.163 10.399 17.020 10.189 16.811C9.980 16.601 9.837 16.334 9.779 16.043C9.721 15.752 9.751 15.450 9.864 15.176C9.978 14.902 10.170 14.668 10.417 14.503C10.663 14.338 10.953 14.250 11.250 14.250C11.648 14.250 12.029 14.408 12.311 14.689C12.592 14.971 12.750 15.352 12.750 15.750ZM21.750 10.500C21.750 10.500 20.218 10.500 20.218 10.500C20.064 8.636 19.330 6.868 18.119 5.442C18.119 5.442 19.281 4.281 19.281 4.281C19.421 4.140 19.500 3.949 19.500 3.750C19.500 3.551 19.421 3.360 19.281 3.219C19.140 3.079 18.949 3.000 18.750 3.000C18.551 3.000 18.360 3.079 18.219 3.219C18.219 3.219 17.063 4.381 17.063 4.381C15.636 3.169 13.865 2.435 12.000 2.282C12.000 2.282 12.000 0.750 12.000 0.750C12.000 0.551 11.921 0.360 11.780 0.220C11.640 0.079 11.449 0.000 11.250 0.000C11.051 0.000 10.860 0.079 10.720 0.220C10.579 0.360 10.500 0.551 10.500 0.750C10.500 0.750 10.500 2.282 10.500 2.282C8.636 2.436 6.868 3.170 5.442 4.381C5.442 4.381 4.281 3.219 4.281 3.219C4.140 3.079 3.949 3.000 3.750 3.000C3.551 3.000 3.360 3.079 3.219 3.219C3.079 3.360 3.000 3.551 3.000 3.750C3.000 3.949 3.079 4.140 3.219 4.281C3.219 4.281 4.381 5.442 4.381 5.442C3.169 6.869 2.435 8.639 2.282 10.505C2.282 10.505 0.750 10.505 0.750 10.505C0.551 10.505 0.360 10.584 0.220 10.724C0.079 10.865 0.000 11.056 0.000 11.255C0.000 11.454 0.079 11.644 0.220 11.785C0.360 11.926 0.551 12.005 0.750 12.005C0.750 12.005 2.282 12.005 2.282 12.005C2.436 13.868 3.170 15.637 4.381 17.063C4.381 17.063 3.219 18.219 3.219 18.219C3.079 18.360 3.000 18.551 3.000 18.750C3.000 18.949 3.079 19.140 3.219 19.281C3.360 19.421 3.551 19.500 3.750 19.500C3.949 19.500 4.140 19.421 4.281 19.281C4.281 19.281 5.442 18.119 5.442 18.119C6.869 19.331 8.639 20.065 10.505 20.218C10.505 20.218 10.505 21.750 10.505 21.750C10.505 21.949 10.584 22.140 10.724 22.280C10.865 22.421 11.056 22.500 11.255 22.500C11.454 22.500 11.644 22.421 11.785 22.280C11.926 22.140 12.005 21.949 12.005 21.750C12.005 21.750 12.005 20.218 12.005 20.218C13.868 20.064 15.637 19.330 17.063 18.119C17.063 18.119 18.224 19.281 18.224 19.281C18.365 19.421 18.556 19.500 18.755 19.500C18.954 19.500 19.145 19.421 19.285 19.281C19.426 19.140 19.505 18.949 19.505 18.750C19.505 18.551 19.426 18.360 19.285 18.219C19.285 18.219 18.119 17.063 18.119 17.063C19.331 15.636 20.065 13.865 20.218 12.000C20.218 12.000 21.750 12.000 21.750 12.000C21.949 12.000 22.140 11.921 22.280 11.780C22.421 11.640 22.500 11.449 22.500 11.250C22.500 11.051 22.421 10.860 22.280 10.720C22.140 10.579 21.949 10.500 21.750 10.500ZM11.250 18.750C9.767 18.750 8.317 18.310 7.083 17.486C5.850 16.662 4.889 15.491 4.321 14.120C3.753 12.750 3.605 11.242 3.894 9.787C4.183 8.332 4.898 6.996 5.947 5.947C6.996 4.898 8.332 4.183 9.787 3.894C11.242 3.605 12.750 3.753 14.120 4.321C15.491 4.889 16.662 5.850 17.486 7.083C18.310 8.317 18.750 9.767 18.750 11.250C18.748 13.238 17.957 15.145 16.551 16.551C15.145 17.957 13.238 18.748 11.250 18.750Z"
      />
    </svg>
  )
}

/**
 * Lab test-tube glyph (filled). Used for the "Biomarkers" tab in the
 * biomarker/disease explorer switcher. Geometry ported from the Paper design.
 */
export function TestTubeIcon({
  width = 24,
  height = 24,
  className,
}: IconProps) {
  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
      className={className}
    >
      <path
        transform="translate(5.5 2.5)"
        d="M6.500 19.000C5.206 19.000 4.104 18.549 3.193 17.648C2.282 16.747 1.827 15.653 1.827 14.365C1.827 14.365 1.827 5.327 1.827 5.327C1.322 5.327 0.891 5.150 0.535 4.797C0.178 4.444 0.000 4.015 0.000 3.510C0.000 3.510 0.000 1.827 0.000 1.827C0.000 1.322 0.178 0.891 0.535 0.535C0.891 0.178 1.322 0.000 1.827 0.000C1.827 0.000 11.163 0.000 11.163 0.000C11.668 0.000 12.101 0.178 12.460 0.535C12.820 0.891 13.000 1.322 13.000 1.827C13.000 1.827 13.000 3.510 13.000 3.510C13.000 4.015 12.820 4.444 12.460 4.797C12.101 5.150 11.668 5.327 11.163 5.327C11.163 5.327 11.163 14.510 11.163 14.510C11.125 15.765 10.654 16.827 9.749 17.696C8.845 18.565 7.762 19.000 6.500 19.000ZM1.827 3.827C1.827 3.827 11.163 3.827 11.163 3.827C11.253 3.827 11.332 3.796 11.399 3.735C11.466 3.675 11.500 3.599 11.500 3.510C11.500 3.510 11.500 1.827 11.500 1.827C11.500 1.737 11.466 1.660 11.399 1.596C11.332 1.532 11.253 1.500 11.163 1.500C11.163 1.500 1.827 1.500 1.827 1.500C1.737 1.500 1.660 1.532 1.596 1.596C1.532 1.660 1.500 1.737 1.500 1.827C1.500 1.827 1.500 3.510 1.500 3.510C1.500 3.599 1.532 3.675 1.596 3.735C1.660 3.796 1.737 3.827 1.827 3.827ZM8.741 16.591C9.356 15.986 9.663 15.244 9.663 14.365C9.663 14.365 9.663 13.990 9.663 13.990C9.663 13.990 6.567 13.990 6.567 13.990C6.567 13.990 6.567 12.491 6.567 12.491C6.567 12.491 9.663 12.491 9.663 12.491C9.663 12.491 9.663 10.067 9.663 10.067C9.663 10.067 6.567 10.067 6.567 10.067C6.567 10.067 6.567 8.567 6.567 8.567C6.567 8.567 9.663 8.567 9.663 8.567C9.663 8.567 9.663 5.327 9.663 5.327C9.663 5.327 3.327 5.327 3.327 5.327C3.327 5.327 3.327 14.365 3.327 14.365C3.327 15.244 3.636 15.986 4.255 16.591C4.873 17.197 5.622 17.500 6.500 17.500C7.378 17.500 8.125 17.197 8.741 16.591Z"
      />
    </svg>
  )
}

// ─── Symptom-group icons (Lucide, stroke 1.5, 24×24) ─────────────────────────
// Used by the symptoms taxonomy (lib/symptom-groups.ts + lib/category-icons.tsx).
// Paths copied from Lucide (lucide.dev) into the repo icon-component format.

/** Lucide `battery-low` — symptom group "Energy & fatigue". */
export function BatteryLowIcon({
  width = 24,
  height = 24,
  className,
  ...props
}: IconProps) {
  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
      className={className}
      {...props}
    >
      <rect
        x="2"
        y="6"
        width="16"
        height="12"
        rx="2"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M22 11v2"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M6 10v4"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

/** Lucide `scale` — symptom group "Weight & metabolism". */
export function ScaleIcon({
  width = 24,
  height = 24,
  className,
  ...props
}: IconProps) {
  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
      className={className}
      {...props}
    >
      <path
        d="m16 16 3-8 3 8c-.87.65-1.92 1-3 1s-2.13-.35-3-1Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="m2 16 3-8 3 8c-.87.65-1.92 1-3 1s-2.13-.35-3-1Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M7 21h10"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M12 3v18"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M3 7h2c2 0 5-1 7-2 2 1 5 2 7 2h2"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

/** Lucide `brain` — symptom group "Mood, sleep & stress". */
export function BrainIcon({
  width = 24,
  height = 24,
  className,
  ...props
}: IconProps) {
  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
      className={className}
      {...props}
    >
      <path
        d="M12 5a3 3 0 1 0-5.997.125 4 4 0 0 0-2.526 5.77 4 4 0 0 0 .556 6.588A4 4 0 1 0 12 18Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M12 5a3 3 0 1 1 5.997.125 4 4 0 0 1 2.526 5.77 4 4 0 0 1-.556 6.588A4 4 0 1 1 12 18Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M15 13a4.5 4.5 0 0 1-3-4 4.5 4.5 0 0 1-3 4"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M17.599 6.5a3 3 0 0 0 .399-1.375"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M6.003 5.125A3 3 0 0 0 6.401 6.5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M3.477 10.896a4 4 0 0 1 .585-.396"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M19.938 10.5a4 4 0 0 1 .585.396"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M6 18a4 4 0 0 1-1.967-.516"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M19.967 17.484A4 4 0 0 1 18 18"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

/** Lucide `venus-and-mars` — symptom group "Hormones & sexual health". */
export function VenusAndMarsIcon({
  width = 24,
  height = 24,
  className,
  ...props
}: IconProps) {
  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
      className={className}
      {...props}
    >
      <path
        d="M10 20h4"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M12 16v6"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M17 2h4v4"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="m21 2-5.46 5.46"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle
        cx="12"
        cy="11"
        r="5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

/** Lucide `salad` — symptom group "Digestion". */
export function SaladIcon({
  width = 24,
  height = 24,
  className,
  ...props
}: IconProps) {
  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
      className={className}
      {...props}
    >
      <path
        d="M7 21h10"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M12 21a9 9 0 0 0 9-9H3a9 9 0 0 0 9 9Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M11.38 12a2.4 2.4 0 0 1-.4-4.77 2.4 2.4 0 0 1 3.2-2.77 2.4 2.4 0 0 1 3.47-.63 2.4 2.4 0 0 1 3.37 3.37 2.4 2.4 0 0 1-1.1 3.7 2.51 2.51 0 0 1 .03 1.1"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="m13 12 4-4"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M10.9 7.25A3.99 3.99 0 0 0 4 10c0 .73.2 1.41.54 2"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

/** Lucide `heart-pulse` — symptom group "Heart & circulation". */
export function HeartPulseIcon({
  width = 24,
  height = 24,
  className,
  ...props
}: IconProps) {
  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
      className={className}
      {...props}
    >
      <path
        d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M3.22 12H9.5l.5-1 2 4.5 2-7 1.5 3.5h5.27"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

/** Lucide `wind` — symptom group "Breathing & airways". */
export function WindIcon({
  width = 24,
  height = 24,
  className,
  ...props
}: IconProps) {
  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
      className={className}
      {...props}
    >
      <path
        d="M12.8 19.6A2 2 0 1 0 14 16H2"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M17.5 8a2.5 2.5 0 1 1 2 4H2"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M9.8 4.4A2 2 0 1 1 11 8H2"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

/** Lucide `bone` — symptom group "Pain, bones & muscles". */
export function BoneIcon({
  width = 24,
  height = 24,
  className,
  ...props
}: IconProps) {
  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
      className={className}
      {...props}
    >
      <path
        d="M17 10c.7-.7 1.69 0 2.5 0a2.5 2.5 0 1 0 0-5 .5.5 0 0 1-.5-.5 2.5 2.5 0 1 0-5 0c0 .81.7 1.8 0 2.5l-7 7c-.7.7-1.69 0-2.5 0a2.5 2.5 0 0 0 0 5c.28 0 .5.22.5.5a2.5 2.5 0 1 0 5 0c0-.81-.7-1.8 0-2.5Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

export function WarningIcon({
  width = 24,
  height = 24,
  className,
  ...props
}: IconProps) {
  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
      className={className}
      {...props}
    >
      <path
        d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M12 9v4M12 17h.01"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

/**
 * Sprout — two leaves on a stem, on the 48px Material grid (fill, not stroke,
 * so it reads solid at the large display sizes the concern LPs use it at).
 * Used as the "the signs add up" wedge glyph on the /go concern landing pages.
 */
export function SproutIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 48 48"
      className={className}
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M22.5 41.5V26.05H21.5C19.3667 26.05 17.3583 25.6417 15.475 24.825C13.5917 24.0083 11.9167 22.85 10.45 21.35C8.98333 19.85 7.875 18.1167 7.125 16.15C6.375 14.1833 6 12.1333 6 10V6H9.916C11.972 6 13.95 6.40833 15.85 7.225C17.75 8.04167 19.45 9.2 20.95 10.7C22.05 11.8333 22.9583 13.1 23.675 14.5C24.3917 15.9 24.9 17.3833 25.2 18.95C25.455 18.5533 25.7353 18.1708 26.041 17.8025C26.347 17.4342 26.6667 17.0667 27 16.7C28.5 15.2 30.2 14.0417 32.1 13.225C34 12.4083 35.9945 12 38.0835 12H42V16C42 18.1333 41.6003 20.1833 40.801 22.15C40.0017 24.1167 38.868 25.85 37.4 27.35C35.9 28.85 34.2073 30 32.322 30.8C30.4367 31.6 28.4627 32 26.4 32H25.5V41.5H22.5ZM22.55 23C22.55 20.9667 22.2167 19.075 21.55 17.325C20.8833 15.575 19.9667 14.0917 18.8 12.875C17.6333 11.6583 16.2 10.7083 14.5 10.025C12.8 9.34167 10.9667 9 9 9C9 11.1 9.30833 13.025 9.925 14.775C10.5417 16.525 11.4333 18.0167 12.6 19.25C14 20.75 15.5083 21.75 17.125 22.25C18.7417 22.75 20.55 23 22.55 23ZM25.5 29C27.5 29 29.35 28.675 31.05 28.025C32.75 27.375 34.1833 26.4417 35.35 25.225C36.5167 24.0083 37.4167 22.525 38.05 20.775C38.6833 19.025 39 17.1 39 15C37 15 35.15 15.3417 33.45 16.025C31.75 16.7083 30.3167 17.65 29.15 18.85C27.7167 20.35 26.75 21.9167 26.25 23.55C25.75 25.1833 25.5 27 25.5 29Z" />
    </svg>
  )
}
